// src/components/Entidades.js
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import LocationPicker from './LocationPicker';
import Logo from './Logo';
import AdditionalItemsModal from './AdditionalItemsModal';

//import { brandIdentity } from "./theme/brandIdentity";

// Importaremos iconos o componentes de UI si es necesario
// import { PlusCircleIcon, BuildingOfficeIcon, TagIcon, MapPinIcon } from '@heroicons/react/24/outline';

// === Nuevos componentes para el rediseño ===

// --- Componente Tooltip ---
function Tooltip({ children, content, preferred = 'top' }) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, placement: 'top' });
  const triggerRef = React.useRef(null);
  const bubbleRef  = React.useRef(null);

  const place = () => {
    if (!triggerRef.current || !bubbleRef.current) return;
    const t = triggerRef.current.getBoundingClientRect();
    const b = bubbleRef.current.getBoundingClientRect();
    const margin = 8;

    // por defecto arriba y centrado
    let top = t.top - b.height - margin;
    let left = t.left + (t.width / 2) - (b.width / 2);
    let placement = 'top';

    // flip a abajo si no cabe
    if (top < margin) {
      top = t.bottom + margin;
      placement = 'bottom';
    }

    // límites laterales
    left = Math.max(margin, Math.min(left, window.innerWidth - b.width - margin));

    setCoords({
      top: Math.round(top),
      left: Math.round(left),
      placement
    });
  };

  useEffect(() => {
    if (!visible) return;
    place();
    const onUpdate = () => place();
    // Escucha scroll/resize/cambios de layout (fase de captura mejora precisión)
    window.addEventListener('scroll', onUpdate, true);
    window.addEventListener('resize', onUpdate, true);
    return () => {
      window.removeEventListener('scroll', onUpdate, true);
      window.removeEventListener('resize', onUpdate, true);
    };
  }, [visible]);

  return (
    <span
      ref={triggerRef}
      className="ti-tooltip-trigger"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {createPortal(
        <div
          ref={bubbleRef}
          className={`ti-tooltip__bubble ${visible ? 'is-visible' : ''} ${coords.placement}`}
          style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 99999 }}
        >
          {content}
        </div>,
        document.body
      )}
    </span>
  );
}

// --- Componente StatusPill ---
function StatusPill({ type, value, tooltip }) {
  const pillContent = (
    <span className={`ti-pill ti-pill--${type}`}>
      {value}
    </span>
  );

  return tooltip ? (
    <Tooltip content={tooltip}>
      {pillContent}
    </Tooltip>
  ) : pillContent;
}

// --- Componente BrandItem (Rediseñado) ---
function BrandItem({
  retailer,
  brand,
  isExpanded,
  isLoading,
  categories,
  toggle,
  contractAssignments,
  loadingContractAssignments,
  onAssignOrChangeContract,
  onRemoveContract,
  onOpenCategoryModal,
  openCatalogAssignment,
  catalogAssignments,
  loadingAssignments,
  getCatalogDisplayName,
  bonusCodesAssignments,
  loadingBonusAssignments,
  onOpenBonusCodesModal,
  getCollectionDisplayName,
  // NUEVO: Props para portabilidad
  portabilityAssignments,
  loadingPortabilityAssignments,
  onOpenPortabilityModal,
  getPortabilitySummary,
  // Props para flujos
  flowAssignments,
  loadingFlowAssignments,
  onAssignOrChangeFlow,
  // Props para adicionales
  additionalItemsData,
  onOpenAdditionalItemsModal
}) {
  // NUEVO: Valores por defecto para props de portabilidad (prevenir errores undefined)
  const safePortabilityAssignments = portabilityAssignments || {};
  const safeLoadingPortabilityAssignments = loadingPortabilityAssignments || new Set();
  const safeOnOpenPortabilityModal = onOpenPortabilityModal || (() => {});

  // Valores por defecto para props de flujos
  const safeFlowAssignments = flowAssignments || {};
  const safeLoadingFlowAssignments = loadingFlowAssignments || new Set();
  const safeOnAssignOrChangeFlow = onAssignOrChangeFlow || (() => {});
  const safeGetPortabilitySummary = getPortabilitySummary || (() => ({ count: 0, totalValue: 0, operators: [] }));

  // Valores por defecto para props de adicionales
  const safeAdditionalItemsData = additionalItemsData || {};
  const safeOnOpenAdditionalItemsModal = onOpenAdditionalItemsModal || (() => {});

  // Calcular estadísticas
  const buybackCategories = categories?.buyback || [];
  const sellCategories = categories?.sell || [];
  
  // Contar catálogos asignados en categorías de retoma
  const catalogsAssigned = buybackCategories.filter(category => {
    const key = `${retailer}-${brand}-${category}`;
    return catalogAssignments[key];
  }).length;
  
  // Contar colecciones asignadas en categorías de venta
  const collectionsAssigned = sellCategories.filter(category => {
    const key = `${retailer}-${brand}-${category}-brand`;
    return bonusCodesAssignments[key];
  }).length;
  
  // Contar colecciones asignadas en categorías de retoma
  const tradersCollectionsAssigned = buybackCategories.filter(category => {
    const key = `${retailer}-${brand}-${category}-trader`;
    return bonusCodesAssignments[key];
  }).length;
  
  // Contar contratos asignados en categorías de retoma
  const contractsAssigned = buybackCategories.filter(category => {
    const key = `${retailer}-${brand}-${category}`;
    return contractAssignments[key];
  }).length;

  // Contar flujos asignados en categorías de retoma
  const flowsAssigned = buybackCategories.filter(category => {
    const key = `${retailer}-${brand}-${category}`;
    return safeFlowAssignments[key];
  }).length;

  // NUEVO: Contar portabilidad asignada en categorías de venta
  const portabilityAssigned = sellCategories.filter(category => {
    const key = `${retailer}-${brand}-${category}`;
    const assignments = safePortabilityAssignments[key];
    return assignments && assignments.length > 0;
  }).length;
  
  const catalogRatio = buybackCategories.length > 0 ? `${catalogsAssigned}/${buybackCategories.length}` : '0/0';
  const catalogStatus = buybackCategories.length === 0 ? 'muted' : catalogsAssigned === buybackCategories.length ? 'ok' : 'warn';
  
  const contractRatio = buybackCategories.length > 0 ? `${contractsAssigned}/${buybackCategories.length}` : '0/0';
  const contractStatus = buybackCategories.length === 0 ? 'muted' : contractsAssigned === buybackCategories.length ? 'ok' : 'warn';

  const flowRatio = buybackCategories.length > 0 ? `${flowsAssigned}/${buybackCategories.length}` : '0/0';
  const flowStatus = buybackCategories.length === 0 ? 'muted' : flowsAssigned === buybackCategories.length ? 'ok' : 'warn';

  // NUEVO: Cálculos para portabilidad (ahora basado en categorías de venta)
  const portabilityRatio = sellCategories.length > 0 ? `${portabilityAssigned}/${sellCategories.length}` : '0/0';
  const portabilityStatus = sellCategories.length === 0 ? 'muted' : portabilityAssigned === sellCategories.length ? 'ok' : portabilityAssigned > 0 ? 'accent' : 'warn';
  
  // Clase de tono general de la marca (portabilidad ahora se evalúa en categorías de venta)
  const allRequiredConfigured = contractsAssigned === buybackCategories.length &&
                               catalogsAssigned === buybackCategories.length &&
                               flowsAssigned === buybackCategories.length &&
                               buybackCategories.length > 0;
  const brandTone = allRequiredConfigured ? 'tone-ok' : 'tone-warn';
  
  return (
    <div className={`ti-brand ${brandTone} ${isExpanded ? 'is-open' : ''}`}>
      {/* Header de la marca */}
      <div className="ti-brand__hdr" onClick={toggle}>
        <div className="ti-brand__title">
          <span className="ti-brand__name">{brand}</span>
          <div className="ti-brand__badges">
            {/* Píldora de contratos por categoría */}
            <StatusPill
              type={contractStatus}
              value={`📄 ${contractRatio}`}
              tooltip={`Categorías con contrato asignado: ${contractsAssigned} de ${buybackCategories.length}`}
            />

            {/* Píldora de flujos por categoría */}
            <StatusPill
              type={flowStatus}
              value={`🌊 ${flowRatio}`}
              tooltip={`Categorías con flujo asignado: ${flowsAssigned} de ${buybackCategories.length}`}
            />

            {/* Píldora de retoma */}
            <StatusPill 
              type={catalogStatus} 
              value={`📥 ${catalogRatio}`} 
              tooltip={`Categorías con catálogo de precios asignado: ${catalogsAssigned} de ${buybackCategories.length}`} 
            />
            
            {/* NUEVO: Píldora de portabilidad (ahora para categorías de venta) */}
            <StatusPill 
              type={portabilityStatus} 
              value={`📱 ${portabilityRatio}`} 
              tooltip={`Categorías de venta con portabilidad configurada: ${portabilityAssigned} de ${sellCategories.length}`} 
            />
            
            {/* Píldora de venta (sin gestión de colecciones) */}
            <StatusPill 
              type="accent" 
              value={`🛍️ ${sellCategories.length}`} 
              tooltip={`Categorías de venta configuradas: ${sellCategories.length}`} 
            />
          </div>
        </div>
        
        <div className="ti-brand__actions">
          <span className={`ti-chevron ${isExpanded ? 'open' : ''}`}>
            {isLoading ? '⏳' : '▼'}
          </span>
        </div>
      </div>
      
      {/* Panel expandible */}
      {isExpanded && (
        <div className="ti-brand__panel">
          <div className="ti-grid">
            
            {/* Tarjeta de Categorías de Retoma */}
            <div className="ti-card ti-card--left-accent indigo">
              <div className="ti-card__hdr">
                <h5 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: 'var(--ti-text)' }}>
                  📥 Categorías que Retomamos
                </h5>
                <button 
                  className="ti-btn ti-btn--link" 
                  onClick={(e) => { e.stopPropagation(); onOpenCategoryModal('buyback'); }}
                >
                  ⚙️
                </button>
              </div>
              
              <div className="ti-tags">
                {buybackCategories.length > 0 ? (
                  buybackCategories.map(category => {
                    const key = `${retailer}-${brand}-${category}`;
                    const isLoadingAssignment = loadingAssignments.has(key);
                    const catalogAssignment = catalogAssignments[key];
                    const hasCatalog = !!catalogAssignment;
                    
                    // Estado del contrato para esta categoría
                    const isLoadingContract = loadingContractAssignments.has(key);
                    const contractAssignment = contractAssignments[key];
                    const hasContract = !!contractAssignment;
                    
                    // Estado de la colección de códigos para esta categoría de retoma
                    const traderCollectionKey = `${retailer}-${brand}-${category}-trader`;
                    const isLoadingTraderCollection = loadingBonusAssignments.has(traderCollectionKey);
                    const traderCollectionAssignment = bonusCodesAssignments[traderCollectionKey];
                    const hasTraderCollection = !!traderCollectionAssignment;

                    // Estado del flujo para esta categoría
                    const isLoadingFlow = safeLoadingFlowAssignments.has(key);
                    const flowAssignment = safeFlowAssignments[key];
                    const hasFlow = !!flowAssignment;

                    // Estado de los items adicionales para esta categoría
                    const additionalItems = safeAdditionalItemsData[key] || [];
                    const hasAdditionalItems = additionalItems.length > 0;
                    const additionalItemsCount = additionalItems.length;

                    return (
                      <div key={category} className="ti-tagRow">
                        <span className={`ti-tag ${hasCatalog && hasContract && hasFlow ? 'ok' : 'warn'}`}>
                          <span className="ti-tag__txt">{category}</span>
                          <span className="ti-tag__dot">
                            {(isLoadingAssignment || isLoadingContract || isLoadingTraderCollection || isLoadingFlow) ? '⏳' : (hasCatalog && hasContract && hasFlow) ? '✓' : '⚠️'}
                          </span>
                        </span>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <Tooltip content={hasCatalog ? `Catálogo: ${getCatalogDisplayName(catalogAssignment)}` : "Gestionar catálogo de precios"}>
                            <button 
                              className="ti-btn ti-btn--ghost" 
                              onClick={(e) => { e.stopPropagation(); openCatalogAssignment(category); }}
                              disabled={isLoadingAssignment}
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              {hasCatalog ? '💰' : '💰➕'}
                            </button>
                          </Tooltip>
                          <Tooltip content={hasContract ? `Contrato: ${contractAssignment.name} v${contractAssignment.version}` : "Gestionar contrato"}>
                            <button 
                              className="ti-btn ti-btn--ghost" 
                              onClick={(e) => { e.stopPropagation(); onAssignOrChangeContract(category); }}
                              disabled={isLoadingContract}
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              {hasContract ? '📄' : '📄➕'}
                            </button>
                          </Tooltip>
                          <Tooltip content={hasTraderCollection ? `Colección: ${getCollectionDisplayName(traderCollectionAssignment)}` : "Gestionar colección de códigos"}>
                            <button
                              className="ti-btn ti-btn--ghost"
                              onClick={(e) => { e.stopPropagation(); onOpenBonusCodesModal(category, 'trader'); }}
                              disabled={isLoadingTraderCollection}
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              {hasTraderCollection ? '🎁' : '🎁➕'}
                            </button>
                          </Tooltip>
                          <Tooltip content={hasFlow ? `Flujo: ${flowAssignment.flow_name} v${flowAssignment.version}` : "Gestionar flujo"}>
                            <button
                              className="ti-btn ti-btn--ghost"
                              onClick={(e) => { e.stopPropagation(); safeOnAssignOrChangeFlow(category); }}
                              disabled={isLoadingFlow}
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              {hasFlow ? '🌊' : '🌊➕'}
                            </button>
                          </Tooltip>
                          <Tooltip content={hasAdditionalItems ? `Adicionales: ${additionalItemsCount} ${additionalItemsCount === 1 ? 'item' : 'items'}` : "Gestionar items adicionales"}>
                            <button
                              className="ti-btn ti-btn--ghost"
                              onClick={(e) => { e.stopPropagation(); safeOnOpenAdditionalItemsModal(category); }}
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              {hasAdditionalItems ? '🧩' : '🧩➕'}
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="ti-empty">Sin categorías configuradas</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Sección de Categorías de Venta */}
          <div className="ti-card ti-card--soft" style={{ marginTop: '12px' }}>
            <div className="ti-card__hdr">
              <h5 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: 'var(--ti-text)' }}>
                🛍️ Categorías que Venden
              </h5>
              <button 
                className="ti-btn ti-btn--link" 
                onClick={(e) => { e.stopPropagation(); onOpenCategoryModal('sell'); }}
              >
                ⚙️
              </button>
            </div>
            
            <div className="ti-tags">
              {sellCategories.length > 0 ? (
                sellCategories.map(category => {
                  // Estado de la portabilidad para esta categoría de venta
                  const portabilityKey = `${retailer}-${brand}-${category}`;
                  const isLoadingPortability = safeLoadingPortabilityAssignments.has(portabilityKey);
                  const portabilityAssignment = safePortabilityAssignments[portabilityKey];
                  const hasPortability = portabilityAssignment && portabilityAssignment.length > 0;
                  const portabilitySummary = hasPortability ? safeGetPortabilitySummary(portabilityAssignment) : null;
                  
                  return (
                    <div key={category} className="ti-tagRow">
                      <span className={`ti-tag ${hasPortability ? 'ok' : 'warn'}`}>
                        <span className="ti-tag__txt">{category}</span>
                        <span className="ti-tag__dot">
                          {isLoadingPortability ? '⏳' : hasPortability ? '✓' : '⚠️'}
                        </span>
                      </span>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <Tooltip content={
                          hasPortability 
                            ? `Portabilidad: ${portabilitySummary.count} operadores (Máx: $${portabilitySummary.totalValue.toLocaleString()})`
                            : "Configurar portabilidad móvil"
                        }>
                          <button 
                            className="ti-btn ti-btn--ghost" 
                            onClick={(e) => { e.stopPropagation(); safeOnOpenPortabilityModal(category); }}
                            disabled={isLoadingPortability}
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                          >
                            {hasPortability ? '📱' : '📱➕'}
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="ti-empty">Sin categorías configuradas</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Componente Modal (Simplificado por ahora) ---
function EntityModal({ isOpen, onClose, onSubmit, entityType, mode, currentValue = '' }) {
  const [inputValue, setInputValue] = useState(currentValue.toUpperCase());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [location, setLocation] = useState(null);

  // Mapeo para traducir tipos de entidad a español para la UI
  const entityTypeMap = {
    company: 'compañía',
    brand: 'marca',
    store: 'tienda'
  };
  const entityNameSpanish = entityTypeMap[entityType] || entityType;
  const entityNameSpanishPlural = entityType === 'company' ? 'compañías' : entityNameSpanish + 's';

  useEffect(() => {
    if (mode === 'edit') {
      setInputValue(currentValue.toUpperCase());
    }
    if (!isOpen) {
        setError('');
        setIsLoading(false);
        setInputValue(''); 
        setLocation(null); // Limpiar ubicación al cerrar modal
    }
  }, [isOpen, mode, currentValue]);

  // Nuevo manejador para el cambio de input con validación
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    // Eliminar # y * y convertir a mayúsculas
    const cleanValue = newValue.replace(/[#*]/g, '').toUpperCase();
    setInputValue(cleanValue);
  };

  const handleLocationSelect = (locationData) => {
    setLocation(locationData);
    // Limpiar error de ubicación si se recibe datos válidos
    if (locationData?.isValid) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!inputValue.trim()) {
      // Usar nombre en español para el mensaje de error
      setError(`El nombre de ${entityNameSpanish} no puede estar vacío.`);
      return;
    }

    // Validar ubicación para tiendas
    if (entityType === 'store') {
      if (!location) {
        setError('Por favor, selecciona la ubicación de la tienda en el mapa.');
        return;
      }
      
      // Verificar que la ubicación sea válida (tenga los campos obligatorios)
      if (!location.isValid) {
        setError('Por favor, completa el campo obligatorio de ciudad.');
        return;
      }
      
      // Verificar específicamente el campo obligatorio
      if (!location.city?.trim()) {
        setError('El campo ciudad es obligatorio para crear la tienda.');
        return;
      }
    }

    setIsLoading(true);
    try {
      // Crear objeto de datos según el tipo de entidad
      let data = { name: inputValue.trim() };
      
      // Para tiendas, incluir los datos de ubicación
      if (entityType === 'store' && location) {
        data = {
          ...data,
          latitude: location.latitude,
          longitude: location.longitude,
          city: location.city?.trim() || '',
          country: location.country?.trim() || '',
          state: location.state?.trim() || '', // department en la API
          neighbourhood: location.neighbourhood?.trim() || '', // district en la API
          postcode: location.postcode?.trim() || ''
        };
      }
      
      await onSubmit(data);
    } catch (apiError) {
      // Usar nombre en español para el mensaje de error
      setError(apiError.message || `Error al ${mode === 'create' ? 'crear' : 'actualizar'} ${entityNameSpanish}.`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // Usar nombre en español para el título
  const title = mode === 'create' 
    ? `Crear nuev${entityType === 'company' ? 'a' : 'a'} ${entityNameSpanish}` 
    : `Editar ${entityNameSpanish}`;

  return (
    <div className="modal-overlay">
      <div className="modal-content animate-fade-in">
        <h3>{title}</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="entityValue" className="form-label">
              {/* Usar nombre en español para la etiqueta */}
              Nombre de {entityNameSpanish}
            </label>
            <input
              id="entityValue"
              type="text"
              className={`form-input ${error && !error.includes('ubicación') && !error.includes('campos obligatorios') ? 'form-input-error' : ''}`}
              value={inputValue}
              onChange={handleInputChange}
              // Usar nombre en español para el placeholder
              placeholder={`Ingrese el nombre de la ${entityNameSpanish}...`}
              autoFocus
              style={{ textTransform: 'uppercase' }}
            />
          </div>

          {entityType === 'store' && (
            <div className="form-group">
              <label className="form-label">Ubicación de la tienda</label>
              <LocationPicker onLocationSelect={handleLocationSelect} />
              {location && !location.isValid && (
                <div className="location-warning">
                  <p className="warning-text">
                    ⚠️ Completa el campo obligatorio de ciudad para continuar
                  </p>
                </div>
              )}
            </div>
          )}
          
          {error && <p className="error-message" style={{ marginTop: '10px' }}>{error}</p>}

          <div className="form-actions modal-actions">
            <button 
              type="button" 
              className="btn btn-outline" 
              onClick={onClose} 
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              className={`btn btn-primary ${isLoading ? 'btn-loading' : ''}`} 
              disabled={isLoading || (entityType === 'store' && (!location || !location.isValid))}
            >
              {isLoading ? 'Guardando...' : (mode === 'create' ? 'Crear' : 'Guardar Cambios')}
            </button>
          </div>
        </form>
      </div>
      <style jsx>{`
         .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.6);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000; /* Asegurar que esté por encima de otros elementos */
         }
         .modal-content {
            background: white;
            padding: 2rem;
            border-radius: 8px;
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
            width: 90%;
            max-width: 600px; /* Aumentado para acomodar el mapa */
            max-height: 90vh;
            overflow-y: auto;
            animation: fadeIn 0.3s ease-out;
         }
         .modal-content h3 {
            margin-top: 0;
            margin-bottom: 1.5rem;
            color: #333;
         }
                 .modal-actions {
          margin-top: 1.5rem;
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 0 1rem;
        }
         .location-warning {
            margin-top: 10px;
            padding: 10px;
            background-color: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: 4px;
         }
         .warning-text {
            color: #856404;
            margin: 0;
            font-size: 0.9rem;
         }
         .btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
         }
       `}</style>
    </div>
  );
}

// --- Componente Modal de Categorías ---
function CategoryModal({ 
  isOpen, 
  isLoading, 
  data, 
  onClose, 
  onSave, 
  showNewCategoryInput,
  setShowNewCategoryInput,
  newCategoryName,
  setNewCategoryName,
  onCreateNewCategory,
  creatingCategory
}) {
  const [selectedCategories, setSelectedCategories] = useState([]);

  useEffect(() => {
    if (isOpen && data.currentCategories) {
      setSelectedCategories([...data.currentCategories]);
    }
  }, [isOpen, data.currentCategories]);

  const handleCategoryToggle = (category) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      } else {
        return [...prev, category];
      }
    });
  };

  const handleSave = () => {
    onSave(selectedCategories);
  };

  const typeLabels = {
    buyback: 'Retoma',
    sell: 'Venta'
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content category-modal">
        <div className="modal-header">
          <h3>Configurar Categorías de {typeLabels[data.type]}</h3>
          <p className="modal-subtitle">
            {data.retailer} - {data.brand}
          </p>
        </div>

        {isLoading ? (
          <div className="modal-loading">
            <div className="loading-spinner"></div>
            <p>Cargando categorías...</p>
          </div>
        ) : (
          <div className="modal-body">
            <div className="categories-selection">
              <div className="section-header">
                <h4>Selecciona las categorías que aplican:</h4>
                <button 
                  className="btn btn-outline btn-sm"
                  onClick={() => setShowNewCategoryInput(!showNewCategoryInput)}
                >
                  + Nueva Categoría
                </button>
              </div>

              {showNewCategoryInput && (
                <div className="new-category-input">
                  <div className="input-group">
                    <input
                      type="text"
                      placeholder="Nombre de la nueva categoría..."
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      className="form-input"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          onCreateNewCategory();
                        }
                      }}
                    />
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={onCreateNewCategory}
                      disabled={!newCategoryName.trim() || creatingCategory}
                    >
                      {creatingCategory ? 'Creando...' : 'Crear'}
                    </button>
                  </div>
                </div>
              )}

              <div className="categories-grid">
                {data.allCategories.map(category => (
                  <label key={category} className="category-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(category)}
                      onChange={() => handleCategoryToggle(category)}
                    />
                    <span className="checkbox-custom"></span>
                    <span className="category-label">{category}</span>
                  </label>
                ))}
              </div>

              {data.allCategories.length === 0 && (
                <p className="empty-state">No hay categorías disponibles. Crea una nueva categoría.</p>
              )}
            </div>

            <div className="selected-summary">
              <h5>Categorías seleccionadas ({selectedCategories.length}):</h5>
              <div className="selected-tags">
                {selectedCategories.map(category => (
                  <span key={category} className={`category-tag ${data.type}-tag`}>
                    {category}
                    <button 
                      className="remove-tag"
                      onClick={() => handleCategoryToggle(category)}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              {selectedCategories.length === 0 && (
                <p className="no-selection">Ninguna categoría seleccionada</p>
              )}
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button 
            className="btn btn-outline"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isLoading}
          >
            Guardar Cambios
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Modal Unificado Reutilizable ---
function UnifiedAssignmentModal({
  isOpen,
  isLoading,
  type, // 'flow', 'contract', 'catalog', 'bonus'
  data, // { retailer, brand, category, current }
  availableItems,
  loadingItems,
  onClose,
  onAssign,
  onRemove,
  renderItem, // función para renderizar cada item de la lista
  getDisplayName // función para obtener el nombre display del item actual
}) {
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedItem(data.current || null);
    }
  }, [isOpen, data.current]);

  const handleAssign = () => {
    if (selectedItem) {
      onAssign(selectedItem);
    }
  };

  if (!isOpen) return null;

  // Configuración por tipo
  const config = {
    flow: {
      title: 'Flujo',
      icon: '🌊',
      color: '#3B82F6',
      colorLight: '#DBEAFE',
      emptyMessage: 'No hay flujos disponibles',
      emptyHint: 'Crea primero un flujo en la sección de Gestión de Flujos.'
    },
    contract: {
      title: 'Contrato',
      icon: '📄',
      color: '#F59E0B',
      colorLight: '#FEF3C7',
      emptyMessage: 'No hay contratos disponibles',
      emptyHint: 'Crea primero un contrato en la sección de Contratos.'
    },
    catalog: {
      title: 'Catálogo de Precios',
      icon: '💰',
      color: '#10B981',
      colorLight: '#D1FAE5',
      emptyMessage: 'No hay catálogos disponibles',
      emptyHint: 'Crea primero un catálogo en la sección de Catálogos.'
    },
    bonus: {
      title: 'Colección de Códigos',
      icon: '🎁',
      color: '#8B5CF6',
      colorLight: '#EDE9FE',
      emptyMessage: 'No hay colecciones disponibles',
      emptyHint: 'Crea primero una colección en la sección de Bonos.'
    }
  };

  const currentConfig = config[type] || config.flow;
  const hasCurrent = !!data.current;

  return (
    <div className="unified-modal-overlay">
      <div className="unified-modal-content">
        {/* Header */}
        <div className="unified-modal-header">
          <div className="unified-modal-title-section">
            <h3 className="unified-modal-title">
              <span className="unified-modal-icon">{currentConfig.icon}</span>
              {hasCurrent ? `Cambiar ${currentConfig.title}` : `Asignar ${currentConfig.title}`}
            </h3>
          </div>
          <div className="unified-modal-context-section">
            <div className="unified-modal-context">
              {data.retailer} • {data.brand} • {data.category}
            </div>
            <button
              className="unified-modal-close-btn"
              onClick={onClose}
              disabled={isLoading}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Current Assignment Section */}
        {hasCurrent && (
          <div className="unified-modal-current" style={{
            background: currentConfig.colorLight,
            borderColor: currentConfig.color
          }}>
            <div className="unified-modal-current-label">
              <span className="unified-modal-current-icon">{currentConfig.icon}</span>
              {currentConfig.title} actual:
            </div>
            <div className="unified-modal-current-value">
              {getDisplayName ? String(getDisplayName(data.current)) : String(data.current.name || 'Sin nombre')}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="unified-modal-body">
          {loadingItems ? (
            <div className="unified-modal-loading">
              <div className="unified-modal-spinner"></div>
              <p>Cargando {currentConfig.title.toLowerCase()}s disponibles...</p>
            </div>
          ) : (
            <div className="unified-modal-selection">
              <h4 className="unified-modal-selection-title">
                Seleccionar {currentConfig.title}
              </h4>

              {availableItems.length === 0 ? (
                <div className="unified-modal-empty">
                  <p>😕 {currentConfig.emptyMessage}</p>
                  <p className="unified-modal-empty-hint">{currentConfig.emptyHint}</p>
                </div>
              ) : (
                <div className="unified-modal-items-list">
                  {availableItems.map(item => (
                    <div
                      key={item.id || item.flow_id || item.contract_id || item.collection_id}
                      className={`unified-modal-item ${selectedItem && (selectedItem.id === item.id || selectedItem.flow_id === item.flow_id || selectedItem.contract_id === item.contract_id) ? 'selected' : ''}`}
                      onClick={() => setSelectedItem(item)}
                      style={{
                        borderColor: selectedItem && (selectedItem.id === item.id || selectedItem.flow_id === item.flow_id || selectedItem.contract_id === item.contract_id) ? currentConfig.color : '#E5E7EB'
                      }}
                    >
                      {renderItem ? renderItem(item, selectedItem) : (
                        <div className="unified-modal-item-content">
                          <div className="unified-modal-item-name">{String(item.name || item.id || 'Sin nombre')}</div>
                          {item.version && (
                            <div className="unified-modal-item-version">v{item.version}</div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="unified-modal-footer">
          <button
            className="unified-modal-btn unified-modal-btn-secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </button>

          {hasCurrent && onRemove && (
            <button
              className="unified-modal-btn unified-modal-btn-danger"
              onClick={() => onRemove(data.retailer, data.brand, data.category)}
              disabled={isLoading}
            >
              {isLoading ? 'Eliminando...' : 'Eliminar Asignación'}
            </button>
          )}

          <button
            className="unified-modal-btn unified-modal-btn-primary"
            onClick={handleAssign}
            disabled={!selectedItem || isLoading}
          >
            {isLoading ? 'Procesando...' : (hasCurrent ? `Cambiar ${currentConfig.title}` : `Asignar ${currentConfig.title}`)}
          </button>
        </div>

        {/* Estilos */}
        <style jsx>{`
          .unified-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 16px;
          }

          .unified-modal-content {
            background: #FFFFFF;
            border-radius: 12px;
            width: 100%;
            max-width: 600px;
            max-height: 80vh;
            overflow: hidden;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
            display: flex;
            flex-direction: column;
          }

          .unified-modal-header {
            padding: 24px;
            border-bottom: 1px solid #E5E7EB;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 16px;
          }

          .unified-modal-title-section {
            flex: 1;
          }

          .unified-modal-title {
            margin: 0;
            font-size: 18px;
            font-weight: 600;
            color: #1F2937;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .unified-modal-icon {
            font-size: 20px;
          }

          .unified-modal-context-section {
            display: flex;
            align-items: center;
            gap: 12px;
          }

          .unified-modal-context {
            font-size: 14px;
            color: #6B7280;
            font-weight: 500;
            text-align: right;
          }

          .unified-modal-close-btn {
            background: #F3F4F6;
            border: none;
            border-radius: 8px;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            color: #6B7280;
            font-size: 14px;
            transition: all 0.2s ease;
          }

          .unified-modal-close-btn:hover:not(:disabled) {
            background: #E5E7EB;
            color: #374151;
          }

          .unified-modal-close-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .unified-modal-current {
            margin: 16px 24px;
            padding: 16px;
            border: 1px solid;
            border-radius: 8px;
          }

          .unified-modal-current-label {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 14px;
            font-weight: 500;
            margin-bottom: 8px;
            color: #374151;
          }

          .unified-modal-current-icon {
            font-size: 16px;
          }

          .unified-modal-current-value {
            font-size: 16px;
            font-weight: 600;
            color: #1F2937;
          }

          .unified-modal-body {
            flex: 1;
            overflow-y: auto;
            padding: 0 24px 16px 24px;
          }

          .unified-modal-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 40px 20px;
            gap: 16px;
            color: #6B7280;
          }

          .unified-modal-spinner {
            width: 24px;
            height: 24px;
            border: 2px solid #E5E7EB;
            border-top: 2px solid #6366F1;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          .unified-modal-selection-title {
            margin: 0 0 16px 0;
            font-size: 16px;
            font-weight: 500;
            color: #374151;
          }

          .unified-modal-empty {
            text-align: center;
            padding: 40px 20px;
            color: #6B7280;
          }

          .unified-modal-empty-hint {
            font-size: 14px;
            color: #9CA3AF;
            margin-top: 8px;
          }

          .unified-modal-items-list {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }

          .unified-modal-item {
            border: 2px solid #E5E7EB;
            border-radius: 8px;
            padding: 16px;
            cursor: pointer;
            transition: all 0.2s ease;
            background: #FFFFFF;
          }

          .unified-modal-item:hover {
            border-color: #6366F1;
            box-shadow: 0 2px 4px rgba(99, 102, 241, 0.1);
          }

          .unified-modal-item.selected {
            background: #F8FAFC;
            box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
          }

          .unified-modal-item-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
          }

          .unified-modal-item-name {
            font-weight: 600;
            color: #1F2937;
          }

          .unified-modal-item-version {
            background: #6366F1;
            color: white;
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 600;
          }

          .unified-modal-footer {
            padding: 24px;
            border-top: 1px solid #E5E7EB;
            display: flex;
            gap: 12px;
            justify-content: flex-end;
          }

          .unified-modal-btn {
            padding: 10px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            border: none;
            min-width: 100px;
          }

          .unified-modal-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .unified-modal-btn-secondary {
            background: #F3F4F6;
            color: #374151;
          }

          .unified-modal-btn-secondary:hover:not(:disabled) {
            background: #E5E7EB;
          }

          .unified-modal-btn-danger {
            background: #EF4444;
            color: white;
          }

          .unified-modal-btn-danger:hover:not(:disabled) {
            background: #DC2626;
          }

          .unified-modal-btn-primary {
            background: #6366F1;
            color: white;
          }

          .unified-modal-btn-primary:hover:not(:disabled) {
            background: #4F46E5;
          }

          .unified-modal-item-description {
            color: #6B7280;
            font-size: 14px;
            margin-top: 4px;
            line-height: 1.4;
          }

          .unified-modal-item-category {
            color: #6366F1;
            font-size: 12px;
            font-weight: 500;
            background: #F0F9FF;
            padding: 2px 6px;
            border-radius: 12px;
            display: inline-block;
            margin-top: 8px;
          }
        `}</style>
      </div>
    </div>
  );
}

// --- Modal de Flujos LEGACY (Eliminado - Usar UnifiedAssignmentModal) ---

// --- Modal para Selección de Contratos ---
function ContractSelectionModal({
  isOpen,
  isLoading,
  data,
  availableContracts,
  loadingContracts,
  onClose,
  onAssign,
  onRemove
}) {
  const [selectedContract, setSelectedContract] = useState(null);
  
  useEffect(() => {
    if (isOpen) {
      setSelectedContract(data.currentContract || null);
    }
  }, [isOpen, data.currentContract]);
  
  const handleAssign = () => {
    if (selectedContract) {
      onAssign(selectedContract);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay">
      <div className="modal-content contract-selection-modal">
        <div className="modal-header">
          <h3 style={{
            margin: '0 0 8px 0',
            color: '#1F2937',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            {data.currentContract ? 'Cambiar Contrato Asignado' : 'Asignar Contrato'}
          </h3>
          <p style={{
            margin: 0,
            color: '#6B7280',
            fontSize: '14px'
          }}>
            {data.retailer} - {data.brand}
          </p>
        </div>
        
        {data.currentContract && (
          <div className="current-contract-info">
            <div className="info-label">
              <span className="contract-icon">📄</span>
              Contrato actual:
            </div>
            <div className="current-contract">
              <strong>{data.currentContract.name}</strong>
              <span className="contract-version">v{data.currentContract.version}</span>
            </div>
          </div>
        )}
        
        {loadingContracts ? (
          <div className="modal-loading">
            <div className="loading-spinner"></div>
            <p>Cargando contratos disponibles...</p>
          </div>
        ) : (
          <div className="modal-body">
            <div className="contract-selection">
              <h4 style={{
                margin: '0 0 16px 0',
                color: '#374151',
                fontSize: '16px',
                fontWeight: '500'
              }}>
                Seleccionar Contrato
              </h4>
              
              {availableContracts.length > 0 ? (
                <div className="contracts-list">
                  {availableContracts.map(contract => (
                    <div key={contract.id} className="contract-option">
                      <label className="contract-radio">
                        <input 
                          type="radio" 
                          name="contract" 
                          value={contract.id}
                          checked={selectedContract?.id === contract.id}
                          onChange={() => setSelectedContract(contract)}
                        />
                        <span className="radio-custom"></span>
                        <div className="contract-info">
                          <span className="contract-name">
                            📄 {contract.name}
                          </span>
                          <span className="contract-details">
                            ID: {contract.id} • Versión: v{contract.version}
                            {data.currentContract?.id === contract.id && (
                              <span className="current-badge">Actual</span>
                            )}
                          </span>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-contracts-message">
                  <p>😕 No hay contratos disponibles</p>
                  <p className="hint">Crea primero un contrato en la sección de Contratos.</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="modal-actions">
          <button 
            className="btn btn-outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </button>
          
          {data.currentContract && (
            <button 
              className="btn btn-danger"
              onClick={() => onRemove(data.retailer, data.brand)}
              disabled={isLoading}
              style={{
                backgroundColor: '#DC2626',
                borderColor: '#DC2626',
                color: 'white'
              }}
            >
              {isLoading ? 'Eliminando...' : 'Eliminar Contrato'}
            </button>
          )}
          
          {availableContracts.length > 0 && (
            <button 
              className="btn btn-primary"
              onClick={handleAssign}
              disabled={isLoading || !selectedContract || (data.currentContract?.id === selectedContract?.id)}
              style={{
                backgroundColor: '#6366F1',
                borderColor: '#6366F1'
              }}
            >
              {isLoading ? 'Procesando...' : (data.currentContract ? 'Cambiar Contrato' : 'Asignar Contrato')}
            </button>
          )}
        </div>
        
        <style jsx>{`
          .contract-selection-modal {
            max-width: 600px;
            width: 95%;
          }
          
          .current-contract-info {
            padding: 16px;
            margin: 16px 0;
            background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
            border: 1px solid #F59E0B;
            border-radius: 8px;
          }
          
          .info-label {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 14px;
            color: #92400E;
            font-weight: 500;
            margin-bottom: 8px;
          }
          
          .contract-icon {
            font-size: 16px;
          }
          
          .current-contract {
            display: flex;
            align-items: center;
            gap: 12px;
            font-size: 16px;
            color: #78350F;
          }
          
          .contract-version {
            background: #F59E0B;
            color: white;
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 600;
          }
          
          .contracts-list {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }
          
          .contract-option {
            border: 1px solid #E5E7EB;
            border-radius: 8px;
            transition: all 0.2s ease;
          }
          
          .contract-option:hover {
            border-color: #6366F1;
            box-shadow: 0 2px 4px rgba(99, 102, 241, 0.1);
          }
          
          .contract-radio {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 16px;
            cursor: pointer;
            width: 100%;
          }
          
          .contract-radio input[type="radio"] {
            display: none;
          }
          
          .radio-custom {
            width: 20px;
            height: 20px;
            border: 2px solid #D1D5DB;
            border-radius: 50%;
            position: relative;
            flex-shrink: 0;
            margin-top: 2px;
            transition: all 0.2s ease;
          }
          
          .contract-radio input[type="radio"]:checked + .radio-custom {
            border-color: #6366F1;
            background-color: #6366F1;
          }
          
          .contract-radio input[type="radio"]:checked + .radio-custom::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 8px;
            height: 8px;
            background: white;
            border-radius: 50%;
          }
          
          .contract-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 4px;
          }
          
          .contract-name {
            font-size: 16px;
            font-weight: 500;
            color: #1F2937;
          }
          
          .contract-details {
            font-size: 14px;
            color: #6B7280;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          
          .current-badge {
            background: #10B981;
            color: white;
            padding: 2px 6px;
            border-radius: 8px;
            font-size: 11px;
            font-weight: 600;
          }
          
          .no-contracts-message {
            text-align: center;
            padding: 32px 16px;
          }
          
          .no-contracts-message p {
            margin: 8px 0;
          }
          
          .no-contracts-message .hint {
            color: #6B7280;
            font-size: 14px;
          }
          
          .btn-danger:hover {
            background-color: #B91C1C !important;
          }

          /* Estilos específicos removidos - ahora se usan los del UnifiedAssignmentModal */
        `}</style>
      </div>
    </div>
  );
}

// --- Modal para Asignación de Catálogos ---
function CatalogAssignmentModal({ 
  isOpen, 
  isLoading, 
  data, 
  availableCatalogs,
  catalogsByCategory,
  onClose, 
  onAssign,
  onRemove,
  getCatalogDisplayName
}) {
  const [selectedCatalog, setSelectedCatalog] = useState('');
  const [isRemoving, setIsRemoving] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      setSelectedCatalog(data.currentAssignment || '');
      setIsRemoving(false);
    }
  }, [isOpen, data.currentAssignment]);
  
  const handleAssign = () => {
    // Permitir asignación tanto para catálogos seleccionados como para quitar asignación (cadena vacía)
    onAssign(selectedCatalog);
  };
  
  const handleRemove = () => {
    setIsRemoving(true);
    onRemove();
  };
  
  if (!isOpen) return null;
  
  // Filtrar catálogos por la categoría seleccionada
  const categoryKey = data.category;
  const availableCatalogsForCategory = catalogsByCategory[categoryKey] || [];
  
  return (
    <div className="modal-overlay">
      <div className="modal-content catalog-assignment-modal">
        <div className="modal-header">
          <h3>Asignar Catálogo de Precios</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="assignment-info">
          <div className="info-row">
            <span className="info-label">Retailer:</span>
            <span className="info-value">{data.retailer}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Marca:</span>
            <span className="info-value">{data.brand}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Categoría:</span>
            <span className="info-value">{data.category}</span>
          </div>
          {data.currentAssignment && (
            <div className="info-row current-assignment">
              <span className="info-label">Catálogo actual:</span>
              <span className="info-value current">
                {getCatalogDisplayName(data.currentAssignment)}
              </span>
            </div>
          )}
        </div>
        
        {isLoading ? (
          <div className="modal-loading">
            <div className="loading-spinner"></div>
            <p>Procesando...</p>
          </div>
        ) : (
          <div className="modal-body">
            <div className="catalog-selection">
              <h4>Seleccionar Catálogo</h4>
              
              {availableCatalogsForCategory.length > 0 ? (
                <div className="catalog-options">
                  <div className="catalog-option">
                    <label className="catalog-radio">
                      <input 
                        type="radio" 
                        name="catalog" 
                        value="" 
                        checked={selectedCatalog === ''}
                        onChange={(e) => setSelectedCatalog(e.target.value)}
                      />
                      <span className="radio-custom"></span>
                      <span className="catalog-label no-assignment">
                        🚫 Sin asignar
                      </span>
                    </label>
                  </div>
                  
                  {availableCatalogsForCategory.map(catalog => (
                    <div key={catalog.fullName} className="catalog-option">
                      <label className="catalog-radio">
                        <input 
                          type="radio" 
                          name="catalog" 
                          value={catalog.fullName}
                          checked={selectedCatalog === catalog.fullName}
                          onChange={(e) => setSelectedCatalog(e.target.value)}
                        />
                        <span className="radio-custom"></span>
                        <span className="catalog-label">
                          📄 {catalog.displayName}
                          {data.currentAssignment === catalog.fullName && (
                            <span className="current-badge">Actual</span>
                          )}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-catalogs-message">
                  <p>🚨 No hay catálogos disponibles para la categoría "{data.category}"</p>
                  <p className="hint">Crea primero un catálogo para esta categoría en la sección de Precios.</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="modal-actions">
          <button 
            className="btn btn-outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </button>
          
          {availableCatalogsForCategory.length > 0 && (
            <button 
              className={`btn ${selectedCatalog === '' ? 'btn-secondary' : 'btn-primary'}`}
              onClick={handleAssign}
              disabled={isLoading || selectedCatalog === data.currentAssignment}
            >
              {selectedCatalog === '' ? 'Quitar Asignación' : 'Asignar Catálogo'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Modal para Asignación de Portabilidad Móvil (NUEVO) ---
function PortabilityAssignmentModal({
  isOpen,
  isLoading,
  data,
  availableOperators,
  onClose,
  onAssign,
  fetchOperatorsFromBackend,
  createOperatorInBackend,
  updateOperatorInBackend,
  deleteOperatorFromBackend,
  showSuccessPopup,
  setApiError
}) {
  const [assignments, setAssignments] = useState([]);
  
  // NUEVO: Estados para CRUD de operadores
  const [operators, setOperators] = useState([]);
  const [showAddOperatorForm, setShowAddOperatorForm] = useState(false);
  const [editingOperator, setEditingOperator] = useState(null);
  const [newOperator, setNewOperator] = useState({ name: '', defaultAmount: '' });
  
  useEffect(() => {
    if (isOpen) {
      setAssignments([...data.currentAssignments]);
      
      // NUEVO: Cargar operadores desde el backend
      const loadOperators = async () => {
        try {
          const operatorsFromBackend = await fetchOperatorsFromBackend();
          setOperators(operatorsFromBackend);
        } catch (error) {
          console.error('Error cargando operadores:', error);
          // Fallback a los datos disponibles si hay error
          setOperators([...availableOperators]);
        }
      };
      
      loadOperators();
      
      // Limpiar formularios
      setShowAddOperatorForm(false);
      setEditingOperator(null);
      setNewOperator({ name: '', defaultAmount: '' });
    }
  }, [isOpen, data.currentAssignments, availableOperators]);
  
  // NUEVO: Función para manejar formato de números con separadores de miles
  const formatNumberWithDots = (value) => {
    // Quitar todo lo que no sea número
    const numericValue = value.replace(/\D/g, '');
    // Agregar puntos como separadores de miles
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };
  
  const parseNumberFromFormatted = (formattedValue) => {
    // Quitar puntos para obtener el número puro
    return formattedValue.replace(/\./g, '');
  };
  
  const handleAmountChange = (e, field) => {
    const rawValue = e.target.value;
    const numericValue = parseNumberFromFormatted(rawValue);
    const formattedValue = formatNumberWithDots(rawValue);
    
    setNewOperator(prev => ({ 
      ...prev, 
      [field]: formattedValue
    }));
  };
  
  const handleAssignmentAmountChange = (operatorId, rawValue) => {
    const formattedValue = formatNumberWithDots(rawValue);
    setAssignments(prev => prev.map(assignment => 
      assignment.operatorId === operatorId
        ? { ...assignment, amount: formattedValue }
        : assignment
    ));
  };
  
  // NUEVO: Funciones CRUD para operadores (CONECTADAS AL BACKEND)
  const handleAddOperator = async () => {
    const numericAmount = parseNumberFromFormatted(newOperator.defaultAmount);
    if (!newOperator.name.trim() || !numericAmount) return;
    
    try {
      // Llamar al backend para crear el operador
      await createOperatorInBackend(newOperator.name.trim(), numericAmount);
      
      // Si es exitoso, agregar al estado local
      const operatorToAdd = {
        id: newOperator.name.toLowerCase().replace(/\s+/g, '_'),
        name: newOperator.name.trim(),
        defaultAmount: numericAmount.toString()
      };
      
      setOperators(prev => [...prev, operatorToAdd]);
      setNewOperator({ name: '', defaultAmount: '' });
      setShowAddOperatorForm(false);
      
      showSuccessPopup(`Operador "${newOperator.name.trim()}" creado exitosamente`);
    } catch (error) {
      setApiError(error.message);
    }
  };
  
  const handleEditOperator = (operator) => {
    setEditingOperator(operator);
    // Formatear el valor para mostrar con puntos
    const formattedAmount = formatNumberWithDots(operator.defaultAmount);
    setNewOperator({ name: operator.name, defaultAmount: formattedAmount });
  };
  
  const handleUpdateOperator = async () => {
    const numericAmount = parseNumberFromFormatted(newOperator.defaultAmount);
    if (!numericAmount) return; // Solo validar el valor, el nombre no cambia
    
    try {
      // Llamar al backend para actualizar el operador
      await updateOperatorInBackend(editingOperator.name, numericAmount);
      
      // Si es exitoso, actualizar el estado local
      setOperators(prev => prev.map(op => 
        op.id === editingOperator.id 
          ? { ...op, defaultAmount: numericAmount.toString() }
          : op
      ));
      
      // Actualizar assignments si el operador editado está asignado (mantener nombre)
      setAssignments(prev => prev.map(assignment => 
        assignment.operatorId === editingOperator.id
          ? { ...assignment, amount: numericAmount.toString() } // Actualizar valor por defecto si no ha sido personalizado
          : assignment
      ));
      
      setEditingOperator(null);
      setNewOperator({ name: '', defaultAmount: '' });
      
      showSuccessPopup(`Valor base de "${editingOperator.name}" actualizado exitosamente`);
    } catch (error) {
      setApiError(error.message);
    }
  };
  
  const handleDeleteOperator = async (operatorId) => {
    const operator = operators.find(op => op.id === operatorId);
    if (!operator) return;
    
    // Confirmar eliminación
    const confirmMessage = `¿Estás seguro de que deseas eliminar el operador "${operator.name}"?\n\nEsta acción no se puede deshacer.`;
    if (!window.confirm(confirmMessage)) return;
    
    try {
      // Llamar al backend para eliminar el operador
      await deleteOperatorFromBackend(operator.name);
      
      // Si es exitoso, eliminar del estado local
      setOperators(prev => prev.filter(op => op.id !== operatorId));
      
      // Eliminar de assignments si está asignado
      setAssignments(prev => prev.filter(assignment => assignment.operatorId !== operatorId));
      
      showSuccessPopup(`Operador "${operator.name}" eliminado exitosamente`);
    } catch (error) {
      setApiError(error.message);
    }
  };
  
  const cancelEdit = () => {
    setEditingOperator(null);
    setNewOperator({ name: '', defaultAmount: '' });
    setShowAddOperatorForm(false);
  };
  
  const addOperator = (operator) => {
    const exists = assignments.find(a => a.operatorId === operator.id);
    if (exists) return;
    
    setAssignments(prev => [...prev, {
      operator: operator.name,
      operatorId: operator.id,
      amount: operator.defaultAmount
    }]);
  };
  
  const removeOperator = (operatorId) => {
    setAssignments(prev => prev.filter(a => a.operatorId !== operatorId));
  };
  
  const updateAmount = (operatorId, newAmount) => {
    setAssignments(prev => prev.map(a => 
      a.operatorId === operatorId ? { ...a, amount: newAmount } : a
    ));
  };
  
  const handleAssign = () => {
    onAssign(assignments);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay">
      <div className="modal-content portability-assignment-modal">
        <div className="modal-header">
          <h3>Configurar Portabilidad Móvil</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="assignment-info">
          <div className="info-row">
            <span className="info-label">Retailer:</span>
            <span className="info-value">{data.retailer}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Marca:</span>
            <span className="info-value">{data.brand}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Categoría:</span>
            <span className="info-value">{data.category}</span>
          </div>
          {assignments.length > 0 && (
            <div className="info-row current-assignment">
              <span className="info-label">Operadores activos:</span>
              <span className="info-value current">
                {assignments.length} configurados
              </span>
            </div>
          )}
        </div>
        
        {isLoading ? (
          <div className="modal-loading">
            <div className="loading-spinner"></div>
            <p>Procesando...</p>
          </div>
        ) : (
          <div className="modal-body">
            <div className="portability-horizontal-layout">
              
              {/* SECCIÓN IZQUIERDA: Operadores Disponibles con CRUD */}
              <div className="operators-section">
                <div className="section-header">
                  <h4>📱 Operadores Disponibles</h4>
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowAddOperatorForm(true)}
                    disabled={editingOperator !== null}
                  >
                    + Nuevo Operador
                  </button>
                </div>
                
                {/* Formulario para agregar/editar operador */}
                {(showAddOperatorForm || editingOperator) && (
                  <div className="operator-form">
                    <h5>{editingOperator ? 'Editar Operador' : 'Nuevo Operador'}</h5>
                    <div className="form-column">
                      <div className="form-group">
                        <label>{editingOperator ? 'Nombre del Operador:' : 'Nombre del Operador:'}:</label>
                        <input 
                          type="text" 
                          value={newOperator.name}
                          onChange={editingOperator ? undefined : (e) => setNewOperator(prev => ({ ...prev, name: e.target.value }))}
                          placeholder={editingOperator ? "" : "Ej: Movistar, Claro, Entel..."}
                          className={`form-input ${editingOperator ? 'form-input-disabled' : ''}`}
                          disabled={editingOperator !== null}
                          title={editingOperator ? 'El nombre del operador no se puede modificar' : ''}
                        />
                        {editingOperator && (
                          <span className="field-note">* El nombre del operador no se puede modificar</span>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Valor Base ($):</label>
                        <div className="amount-input-wrapper">
                          <span className="currency-symbol">$</span>
                          <input 
                            type="text" 
                            value={newOperator.defaultAmount}
                            onChange={(e) => handleAmountChange(e, 'defaultAmount')}
                            placeholder="50.000"
                            className="form-input amount-input"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="form-actions">
                      <button 
                        className="btn btn-outline btn-sm"
                        onClick={cancelEdit}
                      >
                        Cancelar
                      </button>
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={editingOperator ? handleUpdateOperator : handleAddOperator}
                        disabled={!newOperator.name.trim() || !newOperator.defaultAmount.trim()}
                      >
                        {editingOperator ? 'Actualizar' : 'Crear'}
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Lista de operadores */}
                <div className="operators-list">
                  {operators.map(operator => {
                    const isAssigned = assignments.find(a => a.operatorId === operator.id);
                    const isEditing = editingOperator?.id === operator.id;
                    
                    return (
                      <div key={operator.id} className={`operator-item ${
                        isAssigned ? 'assigned' : ''
                      } ${isEditing ? 'editing' : ''}`}>
                        <div className="operator-info">
                          <span className="operator-icon">📱</span>
                          <div className="operator-details">
                            <span className="operator-name">{operator.name}</span>
                            <span className="operator-default">
                              Valor base: ${parseInt(operator.defaultAmount || '0').toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <div className="operator-actions">
                          {!isAssigned ? (
                            <button 
                              className="btn btn-success btn-xs"
                              onClick={() => addOperator(operator)}
                              disabled={isEditing}
                              title="Agregar a configuración"
                            >
                              → Usar
                            </button>
                          ) : (
                            <button 
                              className="btn btn-outline btn-xs"
                              onClick={() => removeOperator(operator.id)}
                              title="Quitar de configuración"
                            >
                              ✗ Quitar
                            </button>
                          )}
                          <button 
                            className="btn btn-outline btn-xs"
                            onClick={() => handleEditOperator(operator)}
                            disabled={showAddOperatorForm}
                            title="Editar operador"
                          >
                            ✏️
                          </button>
                          <button 
                            className="btn btn-danger btn-xs"
                            onClick={() => handleDeleteOperator(operator.id)}
                            disabled={isAssigned || showAddOperatorForm || editingOperator}
                            title="Eliminar operador"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  
                  {operators.length === 0 && (
                    <div className="empty-operators">
                      <p>No hay operadores configurados</p>
                      <p className="hint">Crea tu primer operador con el botón "+Nuevo Operador"</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* SECCIÓN DERECHA: Configuración Actual */}
              <div className="assignments-section">
                <div className="section-header">
                  <h4>⚙️ Configuración Actual</h4>
                  <span className="assignments-count">{assignments.length} activos</span>
                </div>
                
                {assignments.length > 0 ? (
                  <div className="assignments-container">
                    <div className="assignments-list">
                      {assignments.map(assignment => (
                        <div key={assignment.operatorId} className="assignment-item">
                          <div className="assignment-header">
                            <span className="operator-badge">📱 {assignment.operator}</span>
                            <button 
                              className="btn-remove-assignment"
                              onClick={() => removeOperator(assignment.operatorId)}
                              title="Quitar operador"
                            >
                              ×
                            </button>
                          </div>
                          <div className="amount-input-group">
                            <label>Valor ofrecido:</label>
                            <div className="amount-input-wrapper">
                              <span className="currency-symbol">$</span>
                              <input 
                                type="text" 
                                value={formatNumberWithDots(assignment.amount || '0')}
                                onChange={(e) => handleAssignmentAmountChange(assignment.operatorId, e.target.value)}
                                className="amount-input"
                                placeholder="50.000"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* REMOVIDO: Resumen innecesario que quitaba espacio */}
                  </div>
                ) : (
                  <div className="empty-assignments">
                    <div className="empty-icon">📱</div>
                    <h5>Sin operadores configurados</h5>
                    <p>Selecciona operadores de la lista izquierda para comenzar la configuración</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        <div className="modal-actions">
          <button 
            className="btn btn-outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleAssign}
            disabled={isLoading}
          >
            {assignments.length === 0 ? 'Quitar Portabilidad' : 'Guardar Configuración'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Modal para Asignación de Colecciones de Códigos de Bonos ---
function BonusCodesAssignmentModal({
  isOpen,
  isLoading,
  data,
  availableCollections,
  loadingCollections,
  onClose,
  onAssign,
  getCollectionDisplayName
}) {
  const [selectedCollection, setSelectedCollection] = useState('');
  
  useEffect(() => {
    if (isOpen) {
      setSelectedCollection(data.currentAssignment || '');
    }
  }, [isOpen, data.currentAssignment]);
  
  const handleAssign = () => {
    onAssign(selectedCollection);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay">
      <div className="modal-content bonus-codes-assignment-modal">
        <div className="modal-header">
          <h3>Asignar Colección de Códigos</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="assignment-info">
          <div className="info-row">
            <span className="info-label">Retailer:</span>
            <span className="info-value">{data.retailer}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Marca:</span>
            <span className="info-value">{data.brand}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Categoría:</span>
            <span className="info-value">{data.category}</span>
          </div>
          {data.currentAssignment && (
            <div className="info-row current-assignment">
              <span className="info-label">Colección actual:</span>
              <span className="info-value current">
                {getCollectionDisplayName(data.currentAssignment)}
              </span>
            </div>
          )}
        </div>
        
        {isLoading || loadingCollections ? (
          <div className="modal-loading">
            <div className="loading-spinner"></div>
            <p>Cargando colecciones disponibles...</p>
          </div>
        ) : (
          <div className="modal-body">
            <div className="collection-selection">
              <h4>Seleccionar Colección</h4>
              
              {availableCollections.length > 0 ? (
                <div className="collection-options">
                  <div className="collection-option">
                    <label className="collection-radio">
                      <input 
                        type="radio" 
                        name="collection" 
                        value="" 
                        checked={selectedCollection === ''}
                        onChange={(e) => setSelectedCollection(e.target.value)}
                      />
                      <span className="radio-custom"></span>
                      <span className="collection-label no-assignment">
                        🚫 Sin asignar
                      </span>
                    </label>
                  </div>
                  
                  {availableCollections.map(collection => (
                    <div key={collection.id} className="collection-option">
                      <label className="collection-radio">
                        <input 
                          type="radio" 
                          name="collection" 
                          value={collection.id}
                          checked={selectedCollection === collection.id}
                          onChange={(e) => setSelectedCollection(e.target.value)}
                        />
                        <span className="radio-custom"></span>
                        <span className="collection-label">
                          📋 {collection.name}
                          {data.currentAssignment === collection.id && (
                            <span className="current-badge">Actual</span>
                          )}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-collections-message">
                  <p>😕 No hay colecciones de códigos disponibles</p>
                  <p className="hint">Crea primero una colección en la sección de Bonos.</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="modal-actions">
          <button 
            className="btn btn-outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </button>
          
          {availableCollections.length > 0 && (
            <button 
              className={`btn ${selectedCollection === '' ? 'btn-secondary' : 'btn-primary'}`}
              onClick={handleAssign}
              disabled={isLoading || selectedCollection === data.currentAssignment}
            >
              {selectedCollection === '' ? 'Quitar Asignación' : 'Asignar Colección'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Entidades() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null); // Para saber qué compañía está activa
  const [brands, setBrands] = useState([]);
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]); // Nuevo estado para usuarios
  const [features, setFeatures] = useState([]); // Nuevo estado para características
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false); // Para cargar marcas/tiendas
  const [apiError, setApiError] = useState(''); // Renombrado para claridad
  const [viewMode, setViewMode] = useState('brands'); // 'brands', 'stores', 'users' o 'features'
  
  // Estados para manejo de categorías
  const [expandedBrands, setExpandedBrands] = useState(new Set()); // Marcas expandidas
  const [brandCategories, setBrandCategories] = useState({}); // Cache de categorías por marca
  const [loadingCategories, setLoadingCategories] = useState(new Set()); // Marcas cargando categorías
  const [showCategoryModal, setShowCategoryModal] = useState(false); // Modal de categorías
  const [categoryModalData, setCategoryModalData] = useState({
    retailer: '',
    brand: '',
    type: '', // 'buyback' o 'sell'
    currentCategories: [],
    allCategories: []
  });
  const [loadingCategoryModal, setLoadingCategoryModal] = useState(false);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  
  // Estados para gestión de catálogos de precios
  const [availableCatalogs, setAvailableCatalogs] = useState([]);
  const [catalogsByCategory, setCatalogsByCategory] = useState({});
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [catalogAssignments, setCatalogAssignments] = useState({}); // Formato: {"retailer-brand-category": "catalog_name"}
  const [loadingAssignments, setLoadingAssignments] = useState(new Set());
  
  // Estados para gestión de colecciones de códigos de bonos (solo para categorías de retoma/trader)
  const [bonusCodesAssignments, setBonusCodesAssignments] = useState({}); // Formato: {"retailer-brand-category": "collection_id"}
  const [loadingBonusAssignments, setLoadingBonusAssignments] = useState(new Set());
  const [availableBonusCollections, setAvailableBonusCollections] = useState([]);
  const [loadingBonusCollections, setLoadingBonusCollections] = useState(false);
  
  // === NUEVO: Estados para gestión de portabilidad móvil ===
  const [portabilityAssignments, setPortabilityAssignments] = useState({}); // Formato: {"retailer-brand-category": [{operator: "Movistar", amount: "50000"}, ...]}
  const [loadingPortabilityAssignments, setLoadingPortabilityAssignments] = useState(new Set());
  
  // Estados para modal de asignación de portabilidad
  const [showPortabilityModal, setShowPortabilityModal] = useState(false);
  const [portabilityModalData, setPortabilityModalData] = useState({
    retailer: '',
    brand: '',
    category: '',
    currentAssignments: []
  });
  const [loadingPortabilityModal, setLoadingPortabilityModal] = useState(false);
  
  // Estados para gestión de contratos asignados
  const [contractAssignments, setContractAssignments] = useState({}); // Formato: {"retailer-brand": {contract_id, name, version}}
  const [loadingContractAssignments, setLoadingContractAssignments] = useState(new Set());
  const [availableContracts, setAvailableContracts] = useState([]);
  const [loadingAvailableContracts, setLoadingAvailableContracts] = useState(false);
  
  // Estados para modal de selección de contratos
  const [showContractSelectionModal, setShowContractSelectionModal] = useState(false);
  const [contractModalData, setContractModalData] = useState({
    retailer: '',
    brand: '',
    category: '',
    currentContract: null
  });
  const [loadingContractModal, setLoadingContractModal] = useState(false);

  // Estados para gestión de flujos asignados
  const [flowAssignments, setFlowAssignments] = useState({}); // Formato: {"retailer-brand-category": {flow_id, flow_name, version}}
  const [loadingFlowAssignments, setLoadingFlowAssignments] = useState(new Set());
  const [availableFlows, setAvailableFlows] = useState([]);
  const [loadingAvailableFlows, setLoadingAvailableFlows] = useState(false);

  // Estados para modal de selección de flujos
  const [showFlowSelectionModal, setShowFlowSelectionModal] = useState(false);
  const [flowModalData, setFlowModalData] = useState({
    retailer: '',
    brand: '',
    category: '',
    currentFlow: null
  });
  const [loadingFlowModal, setLoadingFlowModal] = useState(false);
  
  // Estados para modal de asignación de catálogos
  const [showCatalogAssignmentModal, setShowCatalogAssignmentModal] = useState(false);
  const [catalogModalData, setCatalogModalData] = useState({
    retailer: '',
    brand: '',
    category: '',
    currentAssignment: ''
  });
  const [loadingCatalogModal, setLoadingCatalogModal] = useState(false);
  
  // Estados para modal de asignación de colecciones de códigos
  const [showBonusCodesAssignmentModal, setShowBonusCodesAssignmentModal] = useState(false);
  const [bonusCodesModalData, setBonusCodesModalData] = useState({
    retailer: '',
    brand: '',
    category: '',
    currentAssignment: '',
    bonusBy: 'brand'
  });
  const [loadingBonusCodesModal, setLoadingBonusCodesModal] = useState(false);

  // Estados para gestión de adicionales
  const [additionalItemsData, setAdditionalItemsData] = useState({}); // Formato: {"retailer-brand-category": [{id, name, icon, states: [{id, name, value, max}]}]}
  const [showAdditionalItemsModal, setShowAdditionalItemsModal] = useState(false);
  const [additionalItemsModalData, setAdditionalItemsModalData] = useState({
    retailer: '',
    brand: '',
    category: ''
  });

  // const [notification, setNotification] = useState({ message: '', type: '' }); // Estado de notificación anterior (eliminado)

  // --- Estado para Modal de Éxito ---
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalMessage, setSuccessModalMessage] = useState('');

  // --- Estado del Modal de Edición/Creación ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' o 'edit'
  const [modalEntityType, setModalEntityType] = useState('company'); // 'company', 'brand', 'store'
  const [modalCurrentValue, setModalCurrentValue] = useState(''); // Para editar
  const [modalTargetValue, setModalTargetValue] = useState(''); // Guardar el valor original para editar/eliminar

  const tenantId = localStorage.getItem('tenant_id') || '';

  // Función para obtener headers con autenticación Cognito
  const getAuthHeaders = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      
      if (!idToken) {
        throw new Error('No se pudo obtener el ID token');
      }
      
      return {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('Error al obtener el token de autenticación:', error);
      throw error;
    }
  };

  // === FUNCIONES PARA BACKEND DE OPERADORES ===
  
  // Función para obtener operadores desde el backend
  const fetchOperatorsFromBackend = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/operadores',
        { headers }
      );
      
      if (response.status === 200 && response.data.operators) {
        // Transformar formato del backend al formato interno
        return response.data.operators.map(op => ({
          id: op.name.toLowerCase().replace(/\s+/g, '_'), // Usar nombre como ID único
          name: op.name,
          defaultAmount: op.base_value.toString()
        }));
      }
      return [];
    } catch (error) {
      console.error('Error al obtener operadores:', error);
      throw error;
    }
  };
  
  // Función para crear operador en el backend
  const createOperatorInBackend = async (name, baseValue) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/operadores',
        {
          name: name,
          base_value: parseInt(baseValue)
        },
        { headers }
      );
      
      return response.status === 201;
    } catch (error) {
      if (error.response?.status === 409) {
        throw new Error('Ya existe un operador con ese nombre');
      }
      console.error('Error al crear operador:', error);
      throw new Error('Error al crear el operador');
    }
  };
  
  // Función para actualizar operador en el backend
  const updateOperatorInBackend = async (name, newBaseValue) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.patch(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/operadores',
        {
          name: name,
          base_value: parseInt(newBaseValue)
        },
        { headers }
      );
      
      return response.status === 200;
    } catch (error) {
      if (error.response?.status === 404) {
        throw new Error('Operador no encontrado');
      }
      console.error('Error al actualizar operador:', error);
      throw new Error('Error al actualizar el operador');
    }
  };
  
  // Función para eliminar operador del backend
  const deleteOperatorFromBackend = async (name) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.delete(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/operadores',
        {
          headers,
          data: { name: name }
        }
      );
      
      return response.status === 200;
    } catch (error) {
      if (error.response?.status === 404) {
        throw new Error('Operador no encontrado');
      }
      console.error('Error al eliminar operador:', error);
      throw new Error('Error al eliminar el operador');
    }
  };

  // --- Función para mostrar el modal de éxito ---
  const showSuccessPopup = (message) => {
    setSuccessModalMessage(message);
    setShowSuccessModal(true);
    setTimeout(() => {
      setShowSuccessModal(false);
      setSuccessModalMessage('');
    }, 2500);
  };

  // --- Funciones para manejo de contratos asignados ---
  
  // Función para obtener contratos disponibles
  const fetchAvailableContracts = async () => {
    setLoadingAvailableContracts(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/contracts_templates?mode=headers',
        { headers }
      );

      if (response.status === 200 && response.data.item?.contracts) {
        const contractsList = [];
        const contractsObj = response.data.item.contracts;
        
        // Procesar los contratos
        Object.entries(contractsObj).forEach(([contractId, contractData]) => {
          if (contractData.M) {
            // Formato DynamoDB
            const name = contractData.M.name?.S || 'Sin nombre';
            const lastVersion = contractData.M.last_version?.S || '0001';
            
            contractsList.push({
              id: contractId,
              name,
              version: lastVersion,
              version_padded: lastVersion
            });
          } else if (contractData.name) {
            // Formato ya procesado
            const name = contractData.name || 'Sin nombre';
            const lastVersion = contractData.last_version || '0001';
            
            contractsList.push({
              id: contractId,
              name,
              version: lastVersion,
              version_padded: lastVersion
            });
          }
        });
        
        setAvailableContracts(contractsList.sort((a, b) => a.name.localeCompare(b.name)));
      }
    } catch (error) {
      console.error('Error al obtener contratos disponibles:', error);
      setApiError('Error al cargar los contratos disponibles.');
    } finally {
      setLoadingAvailableContracts(false);
    }
  };
  
  // Función para obtener contrato asignado a una marca
  const fetchContractAssignment = async (retailer, brand, category) => {
    const key = `${retailer}-${brand}-${category}`;
    const newLoading = new Set(loadingContractAssignments);
    newLoading.add(key);
    setLoadingContractAssignments(newLoading);
    
    console.log(`🔍 Consultando contrato asignado para: ${retailer} - ${brand} - ${category}`);
    
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/contracts_assigns',
        {
          headers,
          params: {
            retailer,
            brand,
            category
          }
        }
      );
      
      console.log(`✅ Respuesta para ${retailer}-${brand}-${category}:`, response.data);
      
      if (response.status === 200 && response.data.item?.valores && response.data.item.valores.length >= 3) {
        // response.data.item.valores es un array con [contract_id, version, name]
        const [contractId, version, name] = response.data.item.valores;
        
        const contractData = {
          contract_id: contractId,
          name: name,
          version: version
        };
        
        console.log(`📄 Contrato encontrado para ${brand}-${category}:`, contractData);
        
        setContractAssignments(prev => ({
          ...prev,
          [key]: contractData
        }));
      } else {
        console.log(`⚪ Sin contrato asignado para ${retailer}-${brand}-${category}`);
        // No hay asignación
        setContractAssignments(prev => {
          const newAssignments = { ...prev };
          delete newAssignments[key];
          return newAssignments;
        });
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.log(`🚫 404 - Sin contrato asignado para ${retailer}-${brand}`);
      } else {
        console.error(`❌ Error al obtener contrato asignado para ${retailer}-${brand}:`, error.response?.data || error.message);
      }
      // En caso de error 404 o cualquier otro error, asumimos que no hay asignación
      setContractAssignments(prev => {
        const newAssignments = { ...prev };
        delete newAssignments[key];
        return newAssignments;
      });
    } finally {
      const newLoading = new Set(loadingContractAssignments);
      newLoading.delete(key);
      setLoadingContractAssignments(newLoading);
    }
  };
  
  // Función para asignar contrato
  const assignContract = async (retailer, brand, category, contractId, versionPadded, name) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/contracts_assigns',
        {
          retailer,
          brand,
          category,
          contract_id: contractId,
          version_padded: versionPadded,
          name
        },
        { headers }
      );
      
      if (response.status === 200 || response.status === 201) {
        const key = `${retailer}-${brand}-${category}`;
        setContractAssignments(prev => ({
          ...prev,
          [key]: {
            contract_id: contractId,
            name,
            version: versionPadded
          }
        }));
        showSuccessPopup('Contrato asignado exitosamente');
        return true;
      }
    } catch (error) {
      console.error('Error al asignar contrato:', error);
      setApiError('Error al asignar el contrato.');
      return false;
    }
  };
  
  // Función para actualizar contrato asignado
  const updateContractAssignment = async (retailer, brand, category, contractId, versionPadded, name) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.patch(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/contracts_assigns',
        {
          retailer,
          brand,
          category,
          contract_id: contractId,
          version_padded: versionPadded,
          name
        },
        { headers }
      );
      
      if (response.status === 200) {
        const key = `${retailer}-${brand}-${category}`;
        setContractAssignments(prev => ({
          ...prev,
          [key]: {
            contract_id: contractId,
            name,
            version: versionPadded
          }
        }));
        showSuccessPopup('Contrato actualizado exitosamente');
        return true;
      }
    } catch (error) {
      console.error('Error al actualizar contrato:', error);
      setApiError('Error al actualizar el contrato.');
      return false;
    }
  };
  
  // Función para eliminar contrato asignado
  const removeContractAssignment = async (retailer, brand, category) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.delete(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/contracts_assigns',
        {
          headers,
          params: {
            retailer,
            brand,
            category
          }
        }
      );
      
      if (response.status === 200) {
        const key = `${retailer}-${brand}-${category}`;
        setContractAssignments(prev => {
          const newAssignments = { ...prev };
          delete newAssignments[key];
          return newAssignments;
        });
        showSuccessPopup('Contrato eliminado exitosamente');
        return true;
      }
    } catch (error) {
      console.error('Error al eliminar contrato:', error);
      setApiError('Error al eliminar el contrato.');
      return false;
    }
  };
  
  // Función para abrir modal de selección de contratos
  const openContractSelectionModal = async (retailer, brand, category) => {
    console.log('🔓 Abriendo modal para contrato:', { retailer, brand, category });
    
    // Cargar contratos disponibles si no están cargados
    if (availableContracts.length === 0) {
      await fetchAvailableContracts();
    }
    
    // Buscar el contrato actual para esta categoría
    const contractKey = `${retailer}-${brand}-${category}`;
    const currentContract = contractAssignments[contractKey] || null;
    
    // Actualizar datos del modal primero
    const newModalData = {
      retailer,
      brand,
      category,
      currentContract
    };
    
    console.log('📝 Estableciendo contractModalData:', newModalData);
    setContractModalData(newModalData);
    
    // Usar un timeout para asegurar que el estado se actualice antes de mostrar el modal
    setTimeout(() => {
      setShowContractSelectionModal(true);
    }, 10);
  };
  
  // Función para manejar la asignación desde el modal
  const handleContractAssignment = async (contract) => {
    const { retailer, brand, category, currentContract } = contractModalData;
    setLoadingContractModal(true);
    
    try {
      let success = false;
      
      if (currentContract) {
        // Actualizar contrato existente
        success = await updateContractAssignment(
          retailer, 
          brand, 
          category,
          contract.id, 
          contract.version_padded, 
          contract.name
        );
      } else {
        // Asignar nuevo contrato
        success = await assignContract(
          retailer, 
          brand, 
          category,
          contract.id, 
          contract.version_padded, 
          contract.name
        );
      }
      
      if (success) {
        setShowContractSelectionModal(false);
      }
    } catch (error) {
      setApiError('Error al procesar la asignación del contrato.');
    } finally {
      setLoadingContractModal(false);
    }
  };
  
  // Función para manejar la eliminación de contrato
  const handleRemoveContract = async (retailerArg, brandArg, categoryArg) => {
    // Usa argumentos si vienen; de lo contrario, cae al estado del modal
    const retailer = retailerArg ?? contractModalData.retailer;
    const brand = brandArg ?? contractModalData.brand;
    const category = categoryArg ?? contractModalData.category;
    
    // Log para debugging
    console.log('🗑️ handleRemoveContract - Params:', { retailer, brand, category });
    
    // Validar que tenemos los datos necesarios
    if (!retailer || !brand || !category) {
      console.error('❌ Datos faltantes para eliminar contrato:', { retailer, brand, category });
      setApiError('Error: Datos de la categoría no disponibles. Intenta cerrar y abrir el modal de nuevo.');
      return;
    }
    
    const confirmMessage = `¿Estás seguro de que deseas eliminar la asignación de contrato para ${brand}?\n\nEsta acción no se puede deshacer.`;
    
    if (window.confirm(confirmMessage)) {
      setLoadingContractModal(true);
      
      try {
        const success = await removeContractAssignment(retailer, brand, category);
        if (success) {
          setShowContractSelectionModal(false);
        }
      } catch (error) {
        setApiError('Error al eliminar la asignación del contrato.');
      } finally {
        setLoadingContractModal(false);
      }
    }
  };

  // --- Funciones para manejo de flujos asignados ---

  // Función para obtener flujos disponibles
  const fetchAvailableFlows = async () => {
    setLoadingAvailableFlows(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/flows?mode=headers',
        { headers }
      );

      if (response.status === 200 && response.data.flows) {
        const flowsList = [];
        const flowsArray = response.data.flows;

        // Procesar los flujos
        flowsArray.forEach(flow => {
          if (flow.flow_id && flow.flow_name) {
            flowsList.push({
              flow_id: flow.flow_id,
              name: flow.flow_name,
              version: flow.version || 1,
              category: flow.category || 'General',
              description: flow.description || ''
            });
          }
        });

        setAvailableFlows(flowsList.sort((a, b) => a.name.localeCompare(b.name)));
      }
    } catch (error) {
      console.error('Error al obtener flujos disponibles:', error);
      setApiError('Error al cargar los flujos disponibles.');
    } finally {
      setLoadingAvailableFlows(false);
    }
  };

  // Función para obtener flujo asignado a una categoría
  const fetchFlowAssignment = async (retailer, brand, category) => {
    const key = `${retailer}-${brand}-${category}`;
    const newLoading = new Set(loadingFlowAssignments);
    newLoading.add(key);
    setLoadingFlowAssignments(newLoading);

    console.log(`🔍 Consultando flujo asignado para: ${retailer} - ${brand} - ${category}`);

    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/flow_assignment',
        {
          headers,
          params: {
            retailer,
            brand,
            category
          }
        }
      );

      console.log(`✅ Respuesta de flujo para ${retailer}-${brand}-${category}:`, response.data);

      if (response.status === 200 && response.data.assignment) {
        const assignment = response.data.assignment;

        const flowData = {
          flow_id: assignment.flow_id,
          flow_name: assignment.flow_name,
          version: assignment.version
        };

        console.log(`🌊 Flujo encontrado para ${brand}-${category}:`, flowData);

        setFlowAssignments(prev => ({
          ...prev,
          [key]: flowData
        }));
      } else {
        console.log(`⚪ Sin flujo asignado para ${retailer}-${brand}-${category}`);
        setFlowAssignments(prev => {
          const newAssignments = { ...prev };
          delete newAssignments[key];
          return newAssignments;
        });
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.log(`🚫 404 - Sin flujo asignado para ${retailer}-${brand}-${category}`);
      } else {
        console.error(`❌ Error al obtener flujo asignado para ${retailer}-${brand}-${category}:`, error.response?.data || error.message);
      }
      setFlowAssignments(prev => {
        const newAssignments = { ...prev };
        delete newAssignments[key];
        return newAssignments;
      });
    } finally {
      const newLoading = new Set(loadingFlowAssignments);
      newLoading.delete(key);
      setLoadingFlowAssignments(newLoading);
    }
  };

  // Función para asignar flujo
  const assignFlow = async (retailer, brand, category, flowId, flowName, version) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/flow_assignment',
        {
          retailer,
          brand,
          category,
          flow_id: flowId,
          flow_name: flowName,
          version
        },
        { headers }
      );

      if (response.status === 200 || response.status === 201) {
        const key = `${retailer}-${brand}-${category}`;
        setFlowAssignments(prev => ({
          ...prev,
          [key]: {
            flow_id: flowId,
            flow_name: flowName,
            version
          }
        }));

        showSuccessPopup(`Flujo "${flowName}" asignado correctamente a ${brand} - ${category}.`);
        return true;
      }
    } catch (error) {
      console.error('Error al asignar flujo:', error);
      setApiError('Error al asignar el flujo.');
      return false;
    }
  };

  // Función para actualizar flujo asignado
  const updateFlowAssignment = async (retailer, brand, category, flowId, flowName, version) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/flow_assignment',
        {
          retailer,
          brand,
          category,
          flow_id: flowId,
          flow_name: flowName,
          version
        },
        { headers }
      );

      if (response.status === 200 || response.status === 201) {
        const key = `${retailer}-${brand}-${category}`;
        setFlowAssignments(prev => ({
          ...prev,
          [key]: {
            flow_id: flowId,
            flow_name: flowName,
            version
          }
        }));

        showSuccessPopup(`Flujo actualizado correctamente para ${brand} - ${category}.`);
        return true;
      }
    } catch (error) {
      console.error('Error al actualizar flujo:', error);
      setApiError('Error al actualizar el flujo.');
      return false;
    }
  };

  // Función para eliminar flujo asignado
  const removeFlowAssignment = async (retailer, brand, category) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.delete(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/flow_assignment',
        {
          headers,
          params: {
            retailer,
            brand,
            category
          }
        }
      );

      if (response.status === 200) {
        const key = `${retailer}-${brand}-${category}`;
        setFlowAssignments(prev => {
          const newAssignments = { ...prev };
          delete newAssignments[key];
          return newAssignments;
        });

        showSuccessPopup(`Asignación de flujo eliminada para ${brand} - ${category}.`);
        return true;
      }
    } catch (error) {
      console.error('Error al eliminar flujo:', error);
      setApiError('Error al eliminar el flujo.');
      return false;
    }
  };

  // Función para abrir modal de selección de flujos
  const onAssignOrChangeFlow = (category, retailer, brand) => {
    console.log('🔓 Abriendo modal para flujo:', { retailer, brand, category });

    // Cargar flujos disponibles si no están cargados
    if (availableFlows.length === 0) {
      fetchAvailableFlows();
    }

    // Buscar el flujo actual para esta categoría
    const key = `${retailer}-${brand}-${category}`;
    const currentFlow = flowAssignments[key] || null;

    setFlowModalData({
      retailer,
      brand,
      category,
      currentFlow
    });

    setShowFlowSelectionModal(true);
  };

  // Función para manejar la selección de flujo en el modal
  const handleFlowSelection = async (flow) => {
    setLoadingFlowModal(true);

    try {
      const { retailer, brand, category, currentFlow } = flowModalData;
      let success = false;

      if (currentFlow) {
        // Actualizar flujo existente
        success = await updateFlowAssignment(
          retailer,
          brand,
          category,
          flow.flow_id,
          flow.name,
          flow.version
        );
      } else {
        // Asignar nuevo flujo
        success = await assignFlow(
          retailer,
          brand,
          category,
          flow.flow_id,
          flow.name,
          flow.version
        );
      }

      if (success) {
        setShowFlowSelectionModal(false);
      }
    } catch (error) {
      setApiError('Error al procesar la asignación del flujo.');
    } finally {
      setLoadingFlowModal(false);
    }
  };

  // Función para manejar la eliminación de flujo
  const handleRemoveFlow = async (retailerArg, brandArg, categoryArg) => {
    const retailer = retailerArg ?? flowModalData.retailer;
    const brand = brandArg ?? flowModalData.brand;
    const category = categoryArg ?? flowModalData.category;

    console.log('🗑️ handleRemoveFlow - Params:', { retailer, brand, category });

    if (!retailer || !brand || !category) {
      console.error('❌ Datos faltantes para eliminar flujo:', { retailer, brand, category });
      setApiError('Error: Datos de la categoría no disponibles. Intenta cerrar y abrir el modal de nuevo.');
      return;
    }

    const confirmMessage = `¿Estás seguro de que deseas eliminar la asignación de flujo para ${brand} - ${category}?\n\nEsta acción no se puede deshacer.`;

    if (window.confirm(confirmMessage)) {
      setLoadingFlowModal(true);

      try {
        const success = await removeFlowAssignment(retailer, brand, category);
        if (success) {
          setShowFlowSelectionModal(false);
        }
      } catch (error) {
        setApiError('Error al eliminar la asignación del flujo.');
      } finally {
        setLoadingFlowModal(false);
      }
    }
  };

  // --- Funciones para manejo de catálogos de precios ---
  
  // Función para obtener catálogos disponibles
  const fetchAvailableCatalogs = async () => {
    setLoadingCatalogs(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
        {
          headers,
          params: {
            info_type: 'catalogs',
            tenant_id: tenantId
          }
        }
      );
      
      const rawCatalogs = response.data.catalogs || [];
      setAvailableCatalogs(rawCatalogs);
      
      // Agrupar catálogos por categoría (formato "categoria#nombre")
      const grouped = {};
      rawCatalogs.forEach(catalogName => {
        if (catalogName.includes('#')) {
          const [category, name] = catalogName.split('#', 2);
          if (!grouped[category]) {
            grouped[category] = [];
          }
          grouped[category].push({
            fullName: catalogName,
            displayName: name
          });
        }
      });
      
      // Ordenar catálogos dentro de cada categoría
      Object.keys(grouped).forEach(category => {
        grouped[category].sort((a, b) => 
          a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' })
        );
      });
      
      setCatalogsByCategory(grouped);
    } catch (error) {
      console.error('Error al obtener catálogos:', error);
      setApiError('Error al cargar los catálogos.');
    } finally {
      setLoadingCatalogs(false);
    }
  };
  
  // Función para obtener asignación de catálogo para una combinación específica
  const fetchCatalogAssignment = async (retailer, brand, category) => {
    const key = `${retailer}-${brand}-${category}`;
    const newLoading = new Set(loadingAssignments);
    newLoading.add(key);
    setLoadingAssignments(newLoading);
    
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/price_assignments',
        {
          headers,
          params: {
            retailer,
            brand,
            category
          }
        }
      );
      
      if (response.status === 200 && response.data.catalog_assigned) {
        setCatalogAssignments(prev => ({
          ...prev,
          [key]: response.data.catalog_assigned
        }));
      } else {
        // No hay asignación
        setCatalogAssignments(prev => {
          const newAssignments = { ...prev };
          delete newAssignments[key];
          return newAssignments;
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error al obtener asignación de catálogo:', error);
      }
      // En caso de error 404 o cualquier otro error, asumimos que no hay asignación
      setCatalogAssignments(prev => {
        const newAssignments = { ...prev };
        delete newAssignments[key];
        return newAssignments;
      });
    } finally {
      const newLoading = new Set(loadingAssignments);
      newLoading.delete(key);
      setLoadingAssignments(newLoading);
    }
  };
  
  // Función para asignar un catálogo
  const assignCatalog = async (retailer, brand, category, catalogName) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/price_assignments',
        {
          retailer,
          brand,
          category,
          new_catalog_assigned: catalogName
        },
        { headers }
      );
      
      if (response.status === 200) {
        const key = `${retailer}-${brand}-${category}`;
        setCatalogAssignments(prev => ({
          ...prev,
          [key]: catalogName
        }));
        showSuccessPopup('Catálogo asignado exitosamente');
        return true;
      }
    } catch (error) {
      console.error('Error al asignar catálogo:', error);
      setApiError('Error al asignar el catálogo.');
      return false;
    }
  };
  
  // Función para eliminar asignación de catálogo
  const removeCatalogAssignment = async (retailer, brand, category) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.delete(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/price_assignments',
        {
          headers,
          params: {
            retailer,
            brand,
            category
          }
        }
      );
      
      if (response.status === 200) {
        const key = `${retailer}-${brand}-${category}`;
        setCatalogAssignments(prev => {
          const newAssignments = { ...prev };
          delete newAssignments[key];
          return newAssignments;
        });
        showSuccessPopup('Asignación de catálogo eliminada exitosamente');
        return true;
      }
    } catch (error) {
      console.error('Error al eliminar asignación de catálogo:', error);
      setApiError('Error al eliminar la asignación del catálogo.');
      return false;
    }
  };
  
  // Función para obtener el nombre de display de un catálogo
  const getCatalogDisplayName = (catalogFullName) => {
    if (!catalogFullName) return '';
    return catalogFullName.includes('#') ? catalogFullName.split('#')[1] : catalogFullName;
  };
  
  // Función para obtener la categoría de un catálogo
  const getCatalogCategory = (catalogFullName) => {
    if (!catalogFullName) return '';
    return catalogFullName.includes('#') ? catalogFullName.split('#')[0] : catalogFullName;
  };
  
  // Función para abrir el modal de asignación de catálogos
  const openCatalogAssignmentModal = async (retailer, brand, category) => {
    // Cargar catálogos disponibles si no están cargados
    if (availableCatalogs.length === 0) {
      await fetchAvailableCatalogs();
    }
    
    const key = `${retailer}-${brand}-${category}`;
    const currentAssignment = catalogAssignments[key] || '';
    
    setCatalogModalData({
      retailer,
      brand,
      category,
      currentAssignment
    });
    
    setShowCatalogAssignmentModal(true);
  };
  
  // Función para manejar la asignación desde el modal
  const handleCatalogAssignment = async (catalogName) => {
    const { retailer, brand, category } = catalogModalData;
    setLoadingCatalogModal(true);
    
    try {
      if (catalogName && catalogName.trim() !== '') {
        // Asignar catálogo
        const success = await assignCatalog(retailer, brand, category, catalogName);
        if (success) {
          setShowCatalogAssignmentModal(false);
        }
      } else {
        // Eliminar asignación
        const success = await removeCatalogAssignment(retailer, brand, category);
        if (success) {
          setShowCatalogAssignmentModal(false);
        }
      }
    } catch (error) {
      setApiError('Error al procesar la asignación del catálogo.');
    } finally {
      setLoadingCatalogModal(false);
    }
  };
  
  // --- Funciones para manejo de colecciones de códigos de bonos ---
  
  // Función para obtener asignación de colección de códigos para una categoría
  const fetchBonusCodesAssignment = async (retailer, brand, category, bonusBy = 'brand') => {
    const key = `${retailer}-${brand}-${category}-${bonusBy}`;
    const newLoading = new Set(loadingBonusAssignments);
    newLoading.add(key);
    setLoadingBonusAssignments(newLoading);
    
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/bonus_codes_assigns',
        {
          headers,
          params: {
            retailer,
            marca: brand,
            categoria: category,
            bonus_by: bonusBy
          }
        }
      );
      
      if (response.status === 200 && response.data.item?.valores) {
        const collectionId = response.data.item.valores;
        setBonusCodesAssignments(prev => ({
          ...prev,
          [key]: collectionId
        }));
      } else {
        // No hay asignación
        setBonusCodesAssignments(prev => {
          const newAssignments = { ...prev };
          delete newAssignments[key];
          return newAssignments;
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error al obtener asignación de códigos de bonos:', error);
      }
      // En caso de error 404 o cualquier otro error, asumimos que no hay asignación
      setBonusCodesAssignments(prev => {
        const newAssignments = { ...prev };
        delete newAssignments[key];
        return newAssignments;
      });
    } finally {
      const newLoading = new Set(loadingBonusAssignments);
      newLoading.delete(key);
      setLoadingBonusAssignments(newLoading);
    }
  };
  
  // Función para asignar una colección de códigos
  const assignBonusCodesCollection = async (retailer, brand, category, collectionId, bonusBy = 'brand') => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/bonus_codes_assigns',
        {
          retailer,
          marca: brand,
          categoria: category,
          collection_id: collectionId,
          bonus_by: bonusBy
        },
        { headers }
      );
      
      if (response.status === 200 || response.status === 201) {
        const key = `${retailer}-${brand}-${category}-${bonusBy}`;
        setBonusCodesAssignments(prev => ({
          ...prev,
          [key]: collectionId
        }));
        showSuccessPopup('Colección de códigos asignada exitosamente');
        return true;
      }
    } catch (error) {
      console.error('Error al asignar colección de códigos:', error);
      setApiError('Error al asignar la colección de códigos.');
      return false;
    }
  };
  
  // Función para actualizar asignación de colección de códigos
  const updateBonusCodesAssignment = async (retailer, brand, category, collectionId, bonusBy = 'brand') => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.patch(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/bonus_codes_assigns',
        {
          retailer,
          marca: brand,
          categoria: category,
          collection_id: collectionId,
          bonus_by: bonusBy
        },
        { headers }
      );
      
      if (response.status === 200) {
        const key = `${retailer}-${brand}-${category}-${bonusBy}`;
        setBonusCodesAssignments(prev => ({
          ...prev,
          [key]: collectionId
        }));
        showSuccessPopup('Colección de códigos actualizada exitosamente');
        return true;
      }
    } catch (error) {
      console.error('Error al actualizar colección de códigos:', error);
      setApiError('Error al actualizar la colección de códigos.');
      return false;
    }
  };
  
  // Función para eliminar asignación de colección de códigos
  const removeBonusCodesAssignment = async (retailer, brand, category, bonusBy = 'brand') => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.delete(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/bonus_codes_assigns',
        {
          headers,
          params: {
            retailer,
            marca: brand,
            categoria: category,
            bonus_by: bonusBy
          }
        }
      );
      
      if (response.status === 200) {
        const key = `${retailer}-${brand}-${category}-${bonusBy}`;
        setBonusCodesAssignments(prev => {
          const newAssignments = { ...prev };
          delete newAssignments[key];
          return newAssignments;
        });
        showSuccessPopup('Asignación de colección de códigos eliminada exitosamente');
        return true;
      }
    } catch (error) {
      console.error('Error al eliminar asignación de colección de códigos:', error);
      setApiError('Error al eliminar la asignación de la colección de códigos.');
      return false;
    }
  };
  
  // Función para obtener colecciones de códigos disponibles desde la API real
  const fetchAvailableBonusCollections = async () => {
    setLoadingBonusCollections(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/bonuses_codes',
        { 
          headers,
          params: { mode: 'all_collections' }
        }
      );
      
      const collectionsData = response.data.collections;
      console.log('🎁 [Entidades] Collections data received:', collectionsData);
      
      // Transformar el objeto en array si es necesario (igual que en BonusCodes.js)
      let collectionsArray = [];
      if (Array.isArray(collectionsData)) {
        collectionsArray = collectionsData;
      } else if (collectionsData && typeof collectionsData === 'object') {
        // Si es un objeto, convertir a array
        collectionsArray = Object.keys(collectionsData).map(collectionId => ({
          id: collectionId,
          name: collectionsData[collectionId].collection_name
        }));
      }
      
      // Ordenar las colecciones por nombre en orden ascendente
      collectionsArray.sort((a, b) => {
        return a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });
      });
      
      setAvailableBonusCollections(collectionsArray);
      console.log('🎁 [Entidades] Available collections:', collectionsArray);
    } catch (error) {
      console.error('Error al obtener colecciones de códigos:', error);
      setApiError('Error al cargar las colecciones de códigos.');
      setAvailableBonusCollections([]); // Asegurar que siempre sea un array
    } finally {
      setLoadingBonusCollections(false);
    }
  };
  
  // Función para abrir el modal de asignación de colecciones de códigos
  const openBonusCodesAssignmentModal = async (retailer, brand, category, bonusBy = 'brand') => {
    // Cargar colecciones disponibles si no están cargadas
    if (availableBonusCollections.length === 0) {
      await fetchAvailableBonusCollections();
    }
    
    const key = `${retailer}-${brand}-${category}-${bonusBy}`;
    const currentAssignment = bonusCodesAssignments[key] || '';
    
    setBonusCodesModalData({
      retailer,
      brand,
      category,
      currentAssignment,
      bonusBy
    });
    
    setShowBonusCodesAssignmentModal(true);
  };

  // Función para abrir el modal de adicionales
  const openAdditionalItemsModal = (retailer, brand, category) => {
    setAdditionalItemsModalData({
      retailer,
      brand,
      category
    });
    setShowAdditionalItemsModal(true);
  };

  // Función para cerrar el modal de adicionales
  const handleCloseAdditionalItemsModal = () => {
    setShowAdditionalItemsModal(false);
  };

  // Función para manejar la asignación de colecciones de códigos desde el modal
  const handleBonusCodesAssignment = async (collectionId) => {
    const { retailer, brand, category, bonusBy } = bonusCodesModalData;
    setLoadingBonusCodesModal(true);
    
    try {
      if (collectionId && collectionId.trim() !== '') {
        // Determinar si es una nueva asignación o actualización
        const currentAssignment = bonusCodesAssignments[`${retailer}-${brand}-${category}-${bonusBy}`];
        let success = false;
        
        if (currentAssignment) {
          // Actualizar asignación existente
          success = await updateBonusCodesAssignment(retailer, brand, category, collectionId, bonusBy);
        } else {
          // Crear nueva asignación
          success = await assignBonusCodesCollection(retailer, brand, category, collectionId, bonusBy);
        }
        
        if (success) {
          setShowBonusCodesAssignmentModal(false);
        }
      } else {
        // Eliminar asignación
        const success = await removeBonusCodesAssignment(retailer, brand, category, bonusBy);
        if (success) {
          setShowBonusCodesAssignmentModal(false);
        }
      }
    } catch (error) {
      setApiError('Error al procesar la asignación de la colección de códigos.');
    } finally {
      setLoadingBonusCodesModal(false);
    }
  };
  
  // Función para obtener el nombre de display de una colección de códigos
  const getCollectionDisplayName = (collectionId) => {
    if (!collectionId) return '';
    const collection = availableBonusCollections.find(c => c.id === collectionId);
    return collection ? collection.name : collectionId;
  };

  // === NUEVO: Funciones para manejo de portabilidad móvil ===
  
  // Función utilitaria para convertir formato visual (con puntos) a número entero
  const parseFormattedNumber = (formattedValue) => {
    if (!formattedValue) return 0;
    // Convertir string a string si es necesario y quitar puntos
    const stringValue = formattedValue.toString();
    const cleanValue = stringValue.replace(/\./g, '');
    return parseInt(cleanValue) || 0;
  };
  
  // Función para obtener asignaciones de portabilidad desde el backend real
  const fetchPortabilityAssignment = async (retailer, brand, category) => {
    const key = `${retailer}-${brand}-${category}`;
    const newLoading = new Set(loadingPortabilityAssignments);
    newLoading.add(key);
    setLoadingPortabilityAssignments(newLoading);
    
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/operadores_assignments',
        {
          headers,
          params: {
            retailer,
            brand,
            category
          }
        }
      );
      
      if (response.status === 200 && response.data.operators && response.data.operators.length > 0) {
        // Transformar formato del backend al formato interno
        const assignments = response.data.operators.map(op => ({
          operator: op.operator_name,
          operatorId: op.operator_name.toLowerCase().replace(/\s+/g, '_'),
          amount: op.operator_amount.toString()
        }));
        
        setPortabilityAssignments(prev => ({
          ...prev,
          [key]: assignments
        }));
      } else {
        // No hay asignaciones
        setPortabilityAssignments(prev => {
          const newAssignments = { ...prev };
          delete newAssignments[key];
          return newAssignments;
        });
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // Sin asignaciones, esto es normal
        console.log(`Sin asignaciones de portabilidad para ${retailer}-${brand}-${category}`);
      } else {
        console.error('Error al obtener asignaciones de portabilidad:', error);
      }
      
      // En caso de error, asumimos que no hay asignaciones
      setPortabilityAssignments(prev => {
        const newAssignments = { ...prev };
        delete newAssignments[key];
        return newAssignments;
      });
    } finally {
      const newLoading = new Set(loadingPortabilityAssignments);
      newLoading.delete(key);
      setLoadingPortabilityAssignments(newLoading);
    }
  };
  
  // Función para crear una asignación de operador
  const createOperatorAssignment = async (retailer, brand, category, operatorName, operatorAmount) => {
    try {
      const headers = await getAuthHeaders();
      const numericAmount = parseFormattedNumber(operatorAmount);
      const response = await axios.post(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/operadores_assignments',
        {
          retailer,
          brand,
          category,
          operator_name: operatorName,
          operator_amount: numericAmount
        },
        { headers }
      );
      
      if (response.status === 201) {
        console.log(`✅ Asignación creada: ${operatorName} para ${retailer}-${brand}-${category}`);
        return true;
      }
    } catch (error) {
      if (error.response?.status === 409) {
        console.log(`⚠️ El operador '${operatorName}' ya tiene asignación en esta ruta`);
        // En caso de conflicto, intentar actualizar en su lugar
        return await updateOperatorAssignment(retailer, brand, category, operatorName, operatorAmount);
      }
      console.error('Error al crear asignación de operador:', error);
      throw error;
    }
    return false;
  };
  
  // Función para actualizar una asignación de operador
  const updateOperatorAssignment = async (retailer, brand, category, operatorName, operatorAmount) => {
    try {
      const headers = await getAuthHeaders();
      const numericAmount = parseFormattedNumber(operatorAmount);
      const response = await axios.patch(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/operadores_assignments',
        {
          retailer,
          brand,
          category,
          operator_name: operatorName,
          operator_amount: numericAmount
        },
        { headers }
      );
      
      if (response.status === 200) {
        console.log(`✅ Asignación actualizada: ${operatorName} para ${retailer}-${brand}-${category}`);
        return true;
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.log(`⚠️ No existe asignación para '${operatorName}', intentando crear...`);
        return await createOperatorAssignment(retailer, brand, category, operatorName, operatorAmount);
      }
      console.error('Error al actualizar asignación de operador:', error);
      throw error;
    }
    return false;
  };
  
  // Función para eliminar una asignación de operador
  const deleteOperatorAssignment = async (retailer, brand, category, operatorName) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.delete(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/operadores_assignments',
        {
          headers,
          data: {
            retailer,
            brand,
            category,
            operator_name: operatorName
          }
        }
      );
      
      if (response.status === 200) {
        console.log(`✅ Asignación eliminada: ${operatorName} para ${retailer}-${brand}-${category}`);
        return true;
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.log(`⚠️ No existe asignación para '${operatorName}' en esta ruta`);
        return true; // Considerar como éxito si no existe
      }
      console.error('Error al eliminar asignación de operador:', error);
      throw error;
    }
    return false;
  };
  
  // Función para abrir modal de portabilidad
  const openPortabilityModal = (retailer, brand, category) => {
    const key = `${retailer}-${brand}-${category}`;
    const currentAssignments = portabilityAssignments[key] || [];
    
    setPortabilityModalData({
      retailer,
      brand,
      category,
      currentAssignments: [...currentAssignments]
    });
    
    setShowPortabilityModal(true);
  };
  
  // Función para manejar la asignación de portabilidad
  const handlePortabilityAssignment = async (assignments) => {
    const { retailer, brand, category } = portabilityModalData;
    setLoadingPortabilityModal(true);
    
    try {
      const key = `${retailer}-${brand}-${category}`;
      const currentAssignments = portabilityAssignments[key] || [];
      
      // Obtener asignaciones actuales del backend para comparar
      const currentOperatorNames = currentAssignments.map(a => a.operator);
      const newOperatorNames = assignments.map(a => a.operator);
      
      // 1. Eliminar operadores que ya no están en la nueva lista
      const operatorsToDelete = currentAssignments.filter(
        current => !newOperatorNames.includes(current.operator)
      );
      
      for (const operatorToDelete of operatorsToDelete) {
        try {
          await deleteOperatorAssignment(retailer, brand, category, operatorToDelete.operator);
        } catch (error) {
          console.warn(`Error al eliminar ${operatorToDelete.operator}:`, error.message);
        }
      }
      
      // 2. Crear o actualizar los operadores en la nueva lista
      const results = [];
      for (const assignment of assignments) {
        try {
          const success = await createOperatorAssignment(
            retailer, 
            brand, 
            category, 
            assignment.operator, 
            assignment.amount
          );
          results.push(success);
        } catch (error) {
          console.error(`Error procesando ${assignment.operator}:`, error.message);
          results.push(false);
        }
      }
      
      // Verificar si al menos algunas operaciones fueron exitosas
      const successCount = results.filter(Boolean).length;
      const totalOperations = assignments.length;
      
      // Actualizar el estado local
      if (assignments.length > 0) {
        setPortabilityAssignments(prev => ({
          ...prev,
          [key]: assignments
        }));
      } else {
        setPortabilityAssignments(prev => {
          const newAssignments = { ...prev };
          delete newAssignments[key];
          return newAssignments;
        });
      }
      
      if (successCount === totalOperations) {
        showSuccessPopup('Portabilidad configurada exitosamente');
      } else if (successCount > 0) {
        showSuccessPopup(`Portabilidad parcialmente configurada (${successCount}/${totalOperations} operadores)`);
      } else if (totalOperations === 0) {
        showSuccessPopup('Asignaciones de portabilidad eliminadas exitosamente');
      } else {
        setApiError('Error al configurar la portabilidad. Intenta nuevamente.');
        return; // No cerrar el modal si hay errores
      }
      
      setShowPortabilityModal(false);
    } catch (error) {
      console.error('Error general en handlePortabilityAssignment:', error);
      setApiError('Error al configurar la portabilidad.');
    } finally {
      setLoadingPortabilityModal(false);
    }
  };
  
  // Función para obtener el resumen de portabilidad
  const getPortabilitySummary = (assignments) => {
    if (!assignments || assignments.length === 0) {
      return { count: 0, totalValue: 0, operators: [] };
    }
    
    const totalValue = assignments.reduce((sum, assignment) => {
      return sum + parseInt(assignment.amount || '0');
    }, 0);
    
    return {
      count: assignments.length,
      totalValue,
      operators: assignments.map(a => a.operator)
    };
  };

  // --- Funciones para manejo de categorías ---
  
  // Función para obtener categorías de un aliado específico
  const fetchBrandCategories = async (retailer, brand, searchType) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/categories',
        {
          headers,
          params: {
            retailer,
            brand,
            search_type: searchType
          }
        }
      );
      return response.data || [];
    } catch (error) {
      console.error(`Error al obtener categorías ${searchType}:`, error);
      return [];
    }
  };

  // Función para obtener todas las categorías generales
  const fetchAllCategories = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/categories',
        { headers }
      );
      return response.data || [];
    } catch (error) {
      console.error('Error al obtener categorías generales:', error);
      return [];
    }
  };

  // Función para crear una nueva categoría general
  const createNewCategory = async (categoryName) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/categories',
        { new_category: categoryName },
        { headers }
      );
      return response.status === 201;
    } catch (error) {
      console.error('Error al crear nueva categoría:', error);
      throw error;
    }
  };

  // Función para actualizar categorías de un aliado
  const updateBrandCategories = async (retailer, brand, searchType, categories) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/categories',
        {
          retailer,
          brand,
          search_type: searchType,
          new_categories_list: categories
        },
        { headers }
      );
      return response.status === 201;
    } catch (error) {
      console.error('Error al actualizar categorías:', error);
      throw error;
    }
  };

  // Función para cargar categorías de una marca (usada tanto para carga inicial como expansión)
  const loadBrandCategoriesData = async (companyName, brand) => {
    const brandKey = `${companyName}-${brand}`;
    
    // Verificar si ya tenemos las categorías en cache
    if (brandCategories[brandKey]) {
      return; // Ya están cargadas
    }
    
    // Cargar colecciones disponibles si no están cargadas (para tooltips)
    if (availableBonusCollections.length === 0) {
      fetchAvailableBonusCollections();
    }
    
    setLoadingCategories(prev => new Set([...prev, brandKey]));
    
    try {
      const [buybackCategories, sellCategories] = await Promise.all([
        fetchBrandCategories(companyName, brand, 'categoriesToBuyback'),
        fetchBrandCategories(companyName, brand, 'categoriesToSell')
      ]);
      
      setBrandCategories(prev => ({
        ...prev,
        [brandKey]: {
          buyback: buybackCategories,
          sell: sellCategories
        }
      }));
      
      // Cargar asignaciones de catálogos para las categorías de retoma
      buybackCategories.forEach(category => {
        fetchCatalogAssignment(companyName, brand, category);
        // Cargar también el contrato asignado para cada categoría de retoma
        fetchContractAssignment(companyName, brand, category);
        // Cargar también el flujo asignado para cada categoría de retoma
        fetchFlowAssignment(companyName, brand, category);
      });
      
      // Cargar asignaciones de colecciones de códigos para las categorías de venta
      sellCategories.forEach(category => {
        fetchBonusCodesAssignment(companyName, brand, category, 'brand');
        // NUEVO: Cargar asignaciones de portabilidad para las categorías de venta
        fetchPortabilityAssignment(companyName, brand, category);
      });
      
      // Cargar asignaciones de colecciones de códigos para las categorías de retoma
      buybackCategories.forEach(category => {
        fetchBonusCodesAssignment(companyName, brand, category, 'trader');
      });
      
    } catch (error) {
      console.error('Error al cargar categorías:', error);
      setApiError('Error al cargar las categorías de la marca.');
    } finally {
      setLoadingCategories(prev => {
        const newSet = new Set(prev);
        newSet.delete(brandKey);
        return newSet;
      });
    }
  };

  // Función para expandir/contraer una marca (simplificada)
  const toggleBrandExpansion = async (brand) => {
    const newExpanded = new Set(expandedBrands);
    const brandKey = `${selectedCompany}-${brand}`;
    
    if (newExpanded.has(brandKey)) {
      // Contraer
      newExpanded.delete(brandKey);
      setExpandedBrands(newExpanded);
    } else {
      // Expandir
      newExpanded.add(brandKey);
      setExpandedBrands(newExpanded);
      
      // Las categorías ya deberían estar cargadas, pero si no lo están, cargarlas
      if (!brandCategories[brandKey]) {
        await loadBrandCategoriesData(selectedCompany, brand);
      }
    }
  };

  // Función para abrir el modal de categorías
  const openCategoryModal = async (brand, type) => {
    setLoadingCategoryModal(true);
    setShowCategoryModal(true);
    
    try {
      const [allCategories, currentCategories] = await Promise.all([
        fetchAllCategories(),
        fetchBrandCategories(selectedCompany, brand, type === 'buyback' ? 'categoriesToBuyback' : 'categoriesToSell')
      ]);
      
      setCategoryModalData({
        retailer: selectedCompany,
        brand,
        type,
        currentCategories,
        allCategories
      });
    } catch (error) {
      console.error('Error al cargar datos del modal:', error);
      setApiError('Error al cargar las categorías.');
    } finally {
      setLoadingCategoryModal(false);
    }
  };

  // Función para manejar la creación de nueva categoría
  const handleCreateNewCategory = async () => {
    if (!newCategoryName.trim()) return;
    
    setCreatingCategory(true);
    try {
      await createNewCategory(newCategoryName.trim());
      
      // Recargar categorías generales
      const updatedCategories = await fetchAllCategories();
      setCategoryModalData(prev => ({
        ...prev,
        allCategories: updatedCategories
      }));
      
      setNewCategoryName('');
      setShowNewCategoryInput(false);
      showSuccessPopup('Categoría creada exitosamente');
    } catch (error) {
      setApiError('Error al crear la nueva categoría.');
    } finally {
      setCreatingCategory(false);
    }
  };

  // Función para guardar categorías seleccionadas
  const handleSaveCategories = async (selectedCategories) => {
    const { retailer, brand, type, currentCategories } = categoryModalData;
    
    // Verificar si hay cambios
    const currentSet = new Set(currentCategories);
    const selectedSet = new Set(selectedCategories);
    
    if (currentSet.size === selectedSet.size && 
        [...currentSet].every(cat => selectedSet.has(cat))) {
      // No hay cambios
      setShowCategoryModal(false);
      return;
    }
    
    try {
      const searchType = type === 'buyback' ? 'categoriesToBuyback' : 'categoriesToSell';
      await updateBrandCategories(retailer, brand, searchType, selectedCategories);
      
      // Actualizar cache local
      const brandKey = `${retailer}-${brand}`;
      setBrandCategories(prev => ({
        ...prev,
        [brandKey]: {
          ...prev[brandKey],
          [type]: selectedCategories
        }
      }));
      
      setShowCategoryModal(false);
      showSuccessPopup('Categorías actualizadas exitosamente');
    } catch (error) {
      setApiError('Error al actualizar las categorías.');
    }
  };

  // --- Carga inicial de compañías ---
  useEffect(() => {
    if (!tenantId) {
        setApiError('Error: No se encontró el Tenant ID. Por favor, inicie sesión.');
        return;
    }
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    setApiError('');
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
        {
          headers,
          params: { 
            info_type: 'companies',
            tenant_id: localStorage.getItem('tenant_id')
          }
        }
      );
      if (response.status === 200 && response.data.companies) {
        // Ordenamos las compañías alfabéticamente para mejor visualización
        setCompanies(response.data.companies.sort());
      } else {
        setApiError('No se pudieron cargar las compañías.');
      }
    } catch (err) {
      console.error('Error al obtener las compañías:', err);
      setApiError('Error al cargar las compañías.');
    } finally {
      setLoadingCompanies(false);
    }
  };

  // --- Carga de detalles (marcas y tiendas) de una compañía ---
  const fetchCompanyDetails = async (companyName) => {
    if (!companyName) return;

    setLoadingDetails(true);
    setApiError('');
    setBrands([]); // Limpiar datos anteriores
    setStores([]);
    setUsers([]); // Limpiar usuarios
    setFeatures([]); // Limpiar características
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
        {
          headers,
          params: {
            info_type: 'create_user_post_company',
            company_name: companyName,
            tenant_id: localStorage.getItem('tenant_id')
          }
        }
      );

      if (response.status === 200) {
        const brandsData = (response.data.brands || []).sort();
        const storesData = (response.data.stores || []).sort();
        
        setBrands(brandsData);
        setStores(storesData);
        
        // Cargar categorías para cada marca automáticamente
        // Los contratos se cargarán después de obtener las categorías
        if (brandsData.length > 0) {
          console.log(`🔍 Cargando categorías para ${brandsData.length} marcas de ${companyName}`);
          brandsData.forEach(brand => {
            loadBrandCategoriesData(companyName, brand);
          });
        }
        
        // Por ahora dejamos los arrays de usuarios y características vacíos
        // setUsers(response.data.users || []);
        // setFeatures(response.data.features || []);
      } else {
         setApiError(`No se pudieron cargar los detalles de ${companyName}.`);
      }
    } catch (err) {
      console.error(`Error al obtener detalles de ${companyName}:`, err);
      setApiError(`Error al cargar los detalles de ${companyName}.`);
    } finally {
      setLoadingDetails(false);
    }
  };

  // --- Lógica de API para Crear/Editar/Eliminar ---
  const callApi = async (method, data, entityType) => {
    // Determinar la URL correcta (puede ser la misma o diferente)
    const url = 'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info';

    // Limpiar data de valores undefined o null si es necesario
    const cleanData = Object.entries(data).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null) {
        acc[key] = value;
      }
      return acc;
    }, {});

    try {
      //console.log(`Calling API: ${method} ${url}`, cleanData); // Log para depuración
      const headers = await getAuthHeaders();
      const response = await axios({
        method: method,
        url: url,
        headers: headers,
        data: cleanData,
      });

      // Ajustar manejo de éxito basado en método
      // POST/PATCH suelen devolver 201 según comentario previo.
      // DELETE podría devolver 200 o 204.
      if (method === 'DELETE') {
          if (response.status === 200 || response.status === 204) {
              return response.data || { success: true }; // Éxito en DELETE
          } else {
              throw new Error(response.data?.message || `Respuesta inesperada en DELETE: ${response.status}`);
          }
      } else { // POST o PATCH
          if (response.status === 201) {
              return response.data; // Éxito
          } else {
              throw new Error(response.data?.message || `Respuesta inesperada: ${response.status}`);
          }
      }
    } catch (err) {
      console.error('Error en llamada API:', err);
      throw new Error(err.response?.data?.message || err.message || 'Error de conexión con la API');
    }
  };

  // --- Manejadores para abrir el Modal ---
  const openModal = (entityType, mode, currentValue = '') => {
    setModalEntityType(entityType);
    setModalMode(mode);
    setModalCurrentValue(currentValue); // Valor a mostrar/editar
    setModalTargetValue(currentValue); // Guardar valor original para PATCH/DELETE
    setIsModalOpen(true);
    setApiError(''); // Limpiar error al abrir modal
  };

  // --- Lógica de Submisión del Modal de Edición/Creación ---
  const handleModalSubmit = async (newValue) => {
    const entityTypeMap = {
      company: 'compañía',
      brand: 'marca',
      store: 'tienda'
    };
    const entityNameSpanish = entityTypeMap[modalEntityType] || modalEntityType;
    
    let data = {};
    let method = 'POST';
    let successMessage = '';

    // Preparar los datos según el tipo de entidad
    if (modalEntityType === 'company') {
      // Para compañías solo necesitamos info_type, tenant_id y new_value
      data = {
        info_type: 'companies',
        tenant_id: tenantId,
        new_value: newValue.name
      };
    } else if (modalEntityType === 'brand') {
      // Para marcas necesitamos info_type, tenant_id, company_name y new_value
      if (!selectedCompany) {
        throw new Error('No hay una compañía seleccionada para la operación.');
      }
      data = {
        info_type: 'brands',
        tenant_id: tenantId,
        company_name: selectedCompany,
        new_value: newValue.name
      };
    } else if (modalEntityType === 'store') {
      // Para tiendas enviamos todos los datos de ubicación
      if (!selectedCompany) {
        throw new Error('No hay una compañía seleccionada para la operación.');
      }

      data = {
        info_type: 'stores',
        tenant_id: tenantId,
        company_name: selectedCompany,
        store_name: newValue.name,
        retailer: selectedCompany, // Usar la compañía seleccionada como retailer
        latitude: newValue.latitude?.toString() || '',
        longitude: newValue.longitude?.toString() || '',
        city: newValue.city || '',
        country: newValue.country || '',
        department: newValue.state || '',
        district: newValue.neighbourhood || '',
        postal_code: newValue.postcode || '',
        new_value: newValue.name
      };
    }

    if (modalMode === 'edit') {
      method = 'PATCH';
      data.old_value = modalTargetValue; // Añadir el valor original
      successMessage = `${entityNameSpanish.charAt(0).toUpperCase() + entityNameSpanish.slice(1)} actualizada exitosamente.`;
    } else {
      method = 'POST';
      successMessage = `${entityNameSpanish.charAt(0).toUpperCase() + entityNameSpanish.slice(1)} creada exitosamente.`;
    }

    try {
      await callApi(method, data, modalEntityType);

      // Actualizar estado local
      if (modalMode === 'create') {
        if (modalEntityType === 'company') {
          setCompanies(prev => [...prev, newValue.name].sort());
        } else if (modalEntityType === 'brand') {
          setBrands(prev => [...prev, newValue.name].sort());
        } else if (modalEntityType === 'store') {
          setStores(prev => [...prev, newValue.name].sort());
        }
      } else { // Modo editar
        if (modalEntityType === 'company') {
          setCompanies(prev => prev.map(c => c === modalTargetValue ? newValue.name : c).sort());
          // Si la compañía editada era la seleccionada, actualizarla
          if (selectedCompany === modalTargetValue) {
            setSelectedCompany(newValue.name);
          }
        } else if (modalEntityType === 'brand') {
          setBrands(prev => prev.map(b => b === modalTargetValue ? newValue.name : b).sort());
        } else if (modalEntityType === 'store') {
          setStores(prev => prev.map(s => s === modalTargetValue ? newValue.name : s).sort());
        }
      }

      showSuccessPopup(successMessage);
      setIsModalOpen(false);
    } catch (apiError) {
      console.error(`Error en handleModalSubmit (${modalMode}):`, apiError);
      setApiError(apiError.message || `Error al ${modalMode === 'create' ? 'crear' : 'actualizar'} ${entityNameSpanish}.`);
    }
  };

  // --- Manejadores de eventos ---
  const handleSelectCompany = (companyName) => {
    if (selectedCompany === companyName) {
      // Si se hace clic en la misma compañía, la deseleccionamos
      setSelectedCompany(null);
      setBrands([]);
      setStores([]);
      // Limpiar estados de categorías, contratos y colecciones de códigos
      setExpandedBrands(new Set());
      setBrandCategories({});
      setLoadingCategories(new Set());
      setContractAssignments({});
      setLoadingContractAssignments(new Set());
      setFlowAssignments({});
      setLoadingFlowAssignments(new Set());
      setBonusCodesAssignments({});
      setLoadingBonusAssignments(new Set());
      // NUEVO: Limpiar estados de portabilidad
      setPortabilityAssignments({});
      setLoadingPortabilityAssignments(new Set());
    } else {
      setSelectedCompany(companyName);
      fetchCompanyDetails(companyName); // Cargar detalles al seleccionar
      setViewMode('brands'); // Por defecto mostrar marcas al seleccionar nueva compañía
      // Limpiar estados de categorías, contratos y colecciones de códigos al cambiar de compañía
      setExpandedBrands(new Set());
      setBrandCategories({});
      setLoadingCategories(new Set());
      setContractAssignments({});
      setLoadingContractAssignments(new Set());
      setFlowAssignments({});
      setLoadingFlowAssignments(new Set());
      setBonusCodesAssignments({});
      setLoadingBonusAssignments(new Set());
      // NUEVO: Limpiar estados de portabilidad al cambiar de compañía
      setPortabilityAssignments({});
      setLoadingPortabilityAssignments(new Set());
    }
  };

  // Modificados para usar openModal
  const handleCreateCompany = () => openModal('company', 'create');
  const handleCreateBrand = () => {
      if (!selectedCompany) return;
      openModal('brand', 'create');
  };
  const handleCreateStore = () => {
      if (!selectedCompany) return;
      openModal('store', 'create');
  };

  // Nuevos Handlers para Editar
  const handleEditCompany = (companyName) => {
      openModal('company', 'edit', companyName);
  };
  const handleEditBrand = (brandName) => {
      if (!selectedCompany) return;
      openModal('brand', 'edit', brandName);
  };
  const handleEditStore = (storeName) => {
      if (!selectedCompany) return;
      openModal('store', 'edit', storeName);
  };

  // --- Renderizado ---
  return (
    <div className="entidades-container">
      {/* --- Modal de Éxito (estilo CreateCampaign) --- */}
      {showSuccessModal && (
        <div className="success-message-overlay">
          <div className="success-message-container" style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            padding: '20px 30px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1050, // Encima del modal de edición
            maxWidth: '90%',
            width: '400px',
            textAlign: 'center',
            animation: 'fadeIn 0.3s ease-out'
          }}>
            <div className="success-icon" style={{
              backgroundColor: '#10B981', // Verde éxito
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              color: 'white',
              fontSize: '32px',
              fontWeight: 'bold'
            }}>
              ✓
            </div>
            <h3 style={{
              margin: '0 0 8px',
              color: '#1F2937',
              fontSize: '18px',
              fontWeight: '600'
            }}>{successModalMessage}</h3>
            {/* Podríamos añadir un texto secundario si fuera necesario */}
          </div>
        </div>
      )}

      <header className="header">
        <Logo 
          variant="horizontal" 
          size="medium" 
          onClick={() => navigate('/dashboard')} 
        />
        <div className="header-title">Gestión de Entidades</div>
        <div className="user-menu">
          <button className="btn btn-outline" onClick={() => navigate('/dashboard')}>Volver al Dashboard</button>
        </div>
      </header>

      <div className="container entities-layout">

        {/* Columna Izquierda: Lista de Compañías */}
        <div className="company-list-column card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 className="column-title">Compañías</h3>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleCreateCompany}
              title="Crear Nueva Compañía"
            >
              {/* <PlusCircleIcon className="icon-sm" /> */}
              <span>+ Crear</span>
            </button>
          </div>

          {loadingCompanies && <div className="loading-spinner-small"></div>}
          {apiError && !loadingCompanies && <p className="error-message">{apiError}</p>}

          {!loadingCompanies && companies.length === 0 && !apiError && (
            <p className="empty-state">No hay compañías registradas.</p>
          )}

          <ul className="company-list">
            {companies.map((company) => (
              <li
                key={company}
                className={`company-list-item ${selectedCompany === company ? 'active' : ''}`}
              >
                <span className="item-text" onClick={() => handleSelectCompany(company)}>{company}</span>
                <div className="item-actions">
                   <button onClick={() => handleEditCompany(company)} className="btn-icon" title="Editar Compañía">
                       ✏️
                   </button>
                   {/* Botón Eliminar - Pendiente */}
                   <button onClick={() => alert('Eliminar pendiente: ' + company)} className="btn-icon btn-icon-danger" title="Eliminar Compañía">
                       🗑️
                   </button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Columna Derecha: Detalles de la Compañía Seleccionada */}
        <div className="details-column card">
          {!selectedCompany ? (
            <div className="empty-state-details">
              <span className="icon-lg">👈</span>
              <h3>Selecciona una Compañía</h3>
              <p>Haz clic en una compañía de la lista para ver o administrar sus marcas y tiendas.</p>
            </div>
          ) : (
            <div className="company-details">
              <h2 className="selected-company-title">{selectedCompany}</h2>

              {/* Selector de Vista (Marcas / Tiendas / Usuarios / Características) */}
              <div className="view-selector">
                <button
                  className={`btn-view-mode ${viewMode === 'brands' ? 'active' : ''}`}
                  onClick={() => setViewMode('brands')}
                >
                  {/* <TagIcon className="icon-sm" /> */}
                  <span>Marcas ({brands.length})</span>
                </button>
                <button
                  className={`btn-view-mode ${viewMode === 'stores' ? 'active' : ''}`}
                  onClick={() => setViewMode('stores')}
                >
                  {/* <MapPinIcon className="icon-sm" /> */}
                   <span>Tiendas ({stores.length})</span>
                </button>
                <button
                  className={`btn-view-mode ${viewMode === 'users' ? 'active' : ''}`}
                  onClick={() => setViewMode('users')}
                >
                  <span>Usuarios ({users.length})</span>
                </button>
                <button
                  className={`btn-view-mode ${viewMode === 'features' ? 'active' : ''}`}
                  onClick={() => setViewMode('features')}
                >
                  <span>Características ({features.length})</span>
                </button>
              </div>

              {loadingDetails && <div className="loading-spinner-small" style={{ margin: '2rem auto' }}></div>}
              {apiError && !loadingDetails && <p className="error-message">{apiError}</p>}

              {/* Contenido de Marcas */}
              {!loadingDetails && viewMode === 'brands' && (
                <div className="details-content animate-fade-in">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                     <h4 className="details-subtitle">Marcas Asociadas</h4>
                     <div style={{ display: 'flex', gap: '8px' }}>
                       <button 
                         className="btn btn-outline btn-sm" 
                         onClick={() => {
                           // Refrescar asignaciones de contratos
                           if (selectedCompany && brands.length > 0) {
                             console.log('🔄 Refrescando asignaciones de contratos...');
                             brands.forEach(brand => {
                               // Recargar categorías y contratos
                               loadBrandCategoriesData(selectedCompany, brand);
                             });
                           }
                         }}
                         title="Refrescar estado de contratos"
                       >
                         🔄 Refrescar
                       </button>
                       <button className="btn btn-secondary btn-sm" onClick={handleCreateBrand}>
                          {/* <PlusCircleIcon className="icon-sm" /> */}
                         <span>+ Crear Marca</span>
                       </button>
                     </div>
                  </div>
                  {brands.length === 0 ? (
                    <p className="empty-state-small">No hay marcas asociadas a esta compañía.</p>
                  ) : (
                    <div className="brands-list">
                      {brands.map(brand => {
                        const brandKey = `${selectedCompany}-${brand}`;
                        const isExpanded = expandedBrands.has(brandKey);
                        const isLoading = loadingCategories.has(brandKey);
                        const categories = brandCategories[brandKey];
                        
                        return (
                          <BrandItem
                            key={brand}
                            retailer={selectedCompany}
                            brand={brand}
                            isExpanded={isExpanded}
                            isLoading={isLoading}
                            categories={categories}
                            toggle={() => toggleBrandExpansion(brand)}
                            contractAssignments={contractAssignments}
                            loadingContractAssignments={loadingContractAssignments}
                            onAssignOrChangeContract={(category) =>
                              openContractSelectionModal(selectedCompany, brand, category)
                            }
                            onRemoveContract={(category) => {
                              setContractModalData({ retailer: selectedCompany, brand, category });
                              handleRemoveContract();
                            }}
                            onOpenCategoryModal={(type) => openCategoryModal(brand, type)}
                            openCatalogAssignment={(category) =>
                              openCatalogAssignmentModal(selectedCompany, brand, category)
                            }
                            catalogAssignments={catalogAssignments}
                            loadingAssignments={loadingAssignments}
                            getCatalogDisplayName={getCatalogDisplayName}
                            bonusCodesAssignments={bonusCodesAssignments}
                            loadingBonusAssignments={loadingBonusAssignments}
                            onOpenBonusCodesModal={(category, bonusBy) =>
                              openBonusCodesAssignmentModal(selectedCompany, brand, category, bonusBy)
                            }
                            getCollectionDisplayName={getCollectionDisplayName}
                            
                            portabilityAssignments={portabilityAssignments}
                            loadingPortabilityAssignments={loadingPortabilityAssignments}
                            onOpenPortabilityModal={(category) =>
                              openPortabilityModal(selectedCompany, brand, category)
                            }
                            getPortabilitySummary={getPortabilitySummary}

                            flowAssignments={flowAssignments}
                            loadingFlowAssignments={loadingFlowAssignments}
                            onAssignOrChangeFlow={(category) =>
                              onAssignOrChangeFlow(category, selectedCompany, brand)
                            }

                            additionalItemsData={additionalItemsData}
                            onOpenAdditionalItemsModal={(category) =>
                              openAdditionalItemsModal(selectedCompany, brand, category)
                            }
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Contenido de Tiendas */}
              {!loadingDetails && viewMode === 'stores' && (
                <div className="details-content animate-fade-in">
                   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                     <h4 className="details-subtitle">Tiendas Asociadas</h4>
                     <button className="btn btn-secondary btn-sm" onClick={handleCreateStore}>
                       {/* <PlusCircleIcon className="icon-sm" /> */ }
                       <span>+ Crear Tienda</span>
                     </button>
                   </div>
                  {stores.length === 0 ? (
                     <p className="empty-state-small">No hay tiendas asociadas a esta compañía.</p>
                  ) : (
                    <ul className="item-list">
                       {stores.map(store => (
                         <li key={store} className="item-list-entry">
                            <span className="item-text">{store}</span>
                            <div className="item-actions">
                                <button onClick={() => handleEditStore(store)} className="btn-icon" title="Editar Tienda">
                                    ✏️
                                </button>
                                {/* Botón Eliminar - Pendiente */}
                                <button onClick={() => alert('Eliminar pendiente: ' + store)} className="btn-icon btn-icon-danger" title="Eliminar Tienda">
                                    🗑️
                                </button>
                            </div>
                         </li>
                       ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Contenido de Usuarios */}
              {!loadingDetails && viewMode === 'users' && (
                <div className="details-content animate-fade-in">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h4 className="details-subtitle">Usuarios Asociados</h4>
                    <button className="btn btn-secondary btn-sm" onClick={() => alert('Crear usuario - Función pendiente')}>
                      <span>+ Crear Usuario</span>
                    </button>
                  </div>
                  <p className="empty-state-small">No hay usuarios asociados a esta compañía.</p>
            </div>
          )}

              {/* Contenido de Características */}
              {!loadingDetails && viewMode === 'features' && (
                <div className="details-content animate-fade-in">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h4 className="details-subtitle">Características Disponibles</h4>
                    <button className="btn btn-secondary btn-sm" onClick={() => alert('Crear característica - Función pendiente')}>
                      <span>+ Crear Característica</span>
                    </button>
                  </div>
                  <p className="empty-state-small">No hay características disponibles para esta compañía.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Renderizar el Modal de Edición/Creación */}
      <EntityModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleModalSubmit} 
        entityType={modalEntityType}
        mode={modalMode}
        currentValue={modalCurrentValue}
      />

      {/* Modal de Categorías */}
      {showCategoryModal && (
        <CategoryModal
          isOpen={showCategoryModal}
          isLoading={loadingCategoryModal}
          data={categoryModalData}
          onClose={() => setShowCategoryModal(false)}
          onSave={handleSaveCategories}
          showNewCategoryInput={showNewCategoryInput}
          setShowNewCategoryInput={setShowNewCategoryInput}
          newCategoryName={newCategoryName}
          setNewCategoryName={setNewCategoryName}
          onCreateNewCategory={handleCreateNewCategory}
          creatingCategory={creatingCategory}
        />
      )}
      
      {/* Modal de Asignación de Catálogos */}
      {showCatalogAssignmentModal && (
        <UnifiedAssignmentModal
          isOpen={showCatalogAssignmentModal}
          isLoading={loadingCatalogModal}
          type="catalog"
          data={{
            retailer: catalogModalData.retailer,
            brand: catalogModalData.brand,
            category: catalogModalData.category,
            current: catalogModalData.currentAssignment ? {
              name: getCatalogDisplayName(catalogModalData.currentAssignment),
              id: catalogModalData.currentAssignment
            } : null
          }}
          availableItems={(() => {
            const categoryKey = catalogModalData.category;
            const availableCatalogsForCategory = catalogsByCategory[categoryKey] || [];
            return availableCatalogsForCategory.map(catalog => ({
              id: catalog.fullName,
              name: catalog.displayName
            }));
          })()}
          loadingItems={loadingCatalogs}
          onClose={() => setShowCatalogAssignmentModal(false)}
          onAssign={(selectedCatalog) => handleCatalogAssignment(selectedCatalog.id)}
          onRemove={() => handleCatalogAssignment('')}
          getDisplayName={(catalog) => catalog.name}
        />
      )}
      
      {/* Modal de Selección de Contratos */}
      {showContractSelectionModal && (
        <UnifiedAssignmentModal
          isOpen={showContractSelectionModal}
          isLoading={loadingContractModal}
          type="contract"
          data={{
            retailer: contractModalData.retailer,
            brand: contractModalData.brand,
            category: contractModalData.category,
            current: contractModalData.currentContract
          }}
          availableItems={availableContracts}
          loadingItems={loadingAvailableContracts}
          onClose={() => setShowContractSelectionModal(false)}
          onAssign={handleContractAssignment}
          onRemove={handleRemoveContract}
          getDisplayName={(contract) => `${contract.name} v${contract.version}`}
        />
      )}
      
      {/* Modal de Asignación de Colecciones de Códigos */}
      {showBonusCodesAssignmentModal && (
        <UnifiedAssignmentModal
          isOpen={showBonusCodesAssignmentModal}
          isLoading={loadingBonusCodesModal}
          type="bonus"
          data={{
            retailer: bonusCodesModalData.retailer,
            brand: bonusCodesModalData.brand,
            category: bonusCodesModalData.category,
            current: bonusCodesModalData.currentAssignment ? {
              name: getCollectionDisplayName(bonusCodesModalData.currentAssignment),
              id: bonusCodesModalData.currentAssignment
            } : null
          }}
          availableItems={availableBonusCollections}
          loadingItems={loadingBonusCollections}
          onClose={() => setShowBonusCodesAssignmentModal(false)}
          onAssign={(selectedCollection) => handleBonusCodesAssignment(selectedCollection.id)}
          getDisplayName={(collection) => collection.name}
        />
      )}
      
      {/* NUEVO: Modal de Asignación de Portabilidad */}
      {/* TODO: Reemplazar con datos reales */}
      {showPortabilityModal && (
        <PortabilityAssignmentModal
          isOpen={showPortabilityModal}
          isLoading={loadingPortabilityModal}
          data={portabilityModalData}
          availableOperators={[]} 
          onClose={() => setShowPortabilityModal(false)}
          onAssign={handlePortabilityAssignment}
          fetchOperatorsFromBackend={fetchOperatorsFromBackend}
          createOperatorInBackend={createOperatorInBackend}
          updateOperatorInBackend={updateOperatorInBackend}
          deleteOperatorFromBackend={deleteOperatorFromBackend}
          showSuccessPopup={showSuccessPopup}
          setApiError={setApiError}
        />
      )}

      {/* Modal de Selección de Flujos */}
      {showFlowSelectionModal && (
        <UnifiedAssignmentModal
          isOpen={showFlowSelectionModal}
          isLoading={loadingFlowModal}
          type="flow"
          data={{
            retailer: flowModalData.retailer,
            brand: flowModalData.brand,
            category: flowModalData.category,
            current: flowModalData.currentFlow
          }}
          availableItems={availableFlows}
          loadingItems={loadingAvailableFlows}
          onClose={() => setShowFlowSelectionModal(false)}
          onAssign={handleFlowSelection}
          onRemove={handleRemoveFlow}
          renderItem={(flow, selectedFlow) => (
            <div className="unified-modal-item-content">
              <div>
                <div className="unified-modal-item-name">{flow.name}</div>
                {flow.description && (
                  <div className="unified-modal-item-description">{flow.description}</div>
                )}
                <div className="unified-modal-item-category">Categoría: {flow.category}</div>
              </div>
              {flow.version && (
                <div className="unified-modal-item-version">v{flow.version}</div>
              )}
            </div>
          )}
          getDisplayName={(flow) => `${flow.flow_name} v${flow.version}`}
        />
      )}

      {/* Modal de Items Adicionales */}
      {showAdditionalItemsModal && (
        <AdditionalItemsModal
          isOpen={showAdditionalItemsModal}
          onClose={handleCloseAdditionalItemsModal}
          retailer={additionalItemsModalData.retailer}
          brand={additionalItemsModalData.brand}
          category={additionalItemsModalData.category}
        />
      )}

      {/* Estilos */}
      <style jsx>{`
        /* === Tokens CSS para el rediseño === */
        :global(:root) {
          --ti-primary: #6366F1; /* indigo */
          --ti-primary-600: #4F46E5;
          --ti-primary-300: #818CF8;
          --ti-green: #10B981;
          --ti-amber: #F59E0B;
          --ti-text: #1F2937;
          --ti-muted: #6B7280;
          --ti-bg: #F9FAFB;
          --ti-white: #FFFFFF;
          --ti-border: #E5E7EB;
        }

        /* === Estilos para el rediseño de marcas === */
        .ti-brand {
          background: var(--ti-white);
          border: 1px solid var(--ti-border);
          border-radius: 10px;
          overflow: hidden;
          transition: box-shadow .2s ease, border-color .2s ease;
        }
        .ti-brand.tone-ok { border-left: 4px solid var(--ti-green); }
        .ti-brand.tone-warn { border-left: 4px solid var(--ti-amber); }
        .ti-brand.is-open { box-shadow: 0 6px 18px rgba(0,0,0,.06); border-color: #D1D5DB; }

        .ti-brand__hdr { 
          display:flex; 
          align-items:center; 
          justify-content:space-between; 
          padding: 12px 14px; 
          cursor:pointer; 
        }
        .ti-brand__title { 
          display:flex; 
          align-items:center; 
          gap:12px; 
          min-width:0; 
        }
        .ti-brand__name { 
          font-weight:600; 
          color:var(--ti-text); 
          font-size:16px; 
        }
        .ti-brand__badges { 
          display:flex; 
          gap:8px; 
          flex-wrap:wrap; 
        }
        .ti-brand__actions { 
          display:flex; 
          align-items:center; 
          gap:8px; 
        }
        .ti-chevron { 
          transition: transform .2s ease; 
        }
        .ti-chevron.open { 
          transform: rotate(180deg); 
        }

        .ti-pill { 
          display:inline-flex; 
          align-items:center; 
          justify-content:center; 
          min-width:28px; 
          height:28px; 
          padding:0 10px; 
          border-radius:9999px; 
          font-size:12px; 
          font-weight:600; 
          border:1px solid transparent; 
        }
        .ti-pill--ok { 
          background:#ECFDF5; 
          color:#065F46; 
          border-color:#A7F3D0; 
        }
        .ti-pill--warn { 
          background:#FFFBEB; 
          color:#92400E; 
          border-color:#FDE68A; 
        }
        .ti-pill--accent { 
          background:#EEF2FF; 
          color:#3730A3; 
          border-color:#C7D2FE; 
        }
        .ti-pill--muted { 
          background:#F3F4F6; 
          color:#4B5563; 
          border-color:#E5E7EB; 
        }
        .ti-pill--neutral { 
          background:#F9FAFB; 
          color:#374151; 
          border-color:#E5E7EB; 
        }

        .ti-tooltip-trigger { position: relative; display: inline-flex; }

        .ti-tooltip__bubble {
          background: #111827; color: #fff; font-size: 12px;
          padding: 6px 10px; border-radius: 6px; pointer-events: none;
          opacity: 0; transform: translateY(-2px);
          transition: opacity .12s ease, transform .12s ease;
          box-shadow: 0 4px 12px rgba(0,0,0,.3); max-width: 320px; text-align: center;
        }
        .ti-tooltip__bubble.is-visible { opacity: 1; transform: translateY(0); }
        
        /* flechita */
        .ti-tooltip__bubble::after {
          content: ""; position: absolute; left: 50%; transform: translateX(-50%);
          border: 6px solid transparent;
        }
        .ti-tooltip__bubble.top::after    { top: 100%;  border-top-color:  #111827; }
        .ti-tooltip__bubble.bottom::after { bottom: 100%; border-bottom-color:#111827; }

        .ti-brand__panel { 
          padding: 8px 14px 16px; 
          background: #F8FAFC; 
          border-top:1px solid var(--ti-border); 
        }
        .ti-grid { 
          display:grid; 
          grid-template-columns:repeat(2, minmax(0,1fr)); 
          gap:12px; 
        }

        .ti-card { 
          background: var(--ti-white); 
          border:1px solid var(--ti-border); 
          border-radius:10px; 
          padding:12px; 
        }
        .ti-card--soft { 
          background: linear-gradient(180deg, #FFFFFF, #FAFAFF); 
          border-color:#EAEAFB; 
        }
        .ti-card--left-accent { 
          position:relative; 
        }
        .ti-card--left-accent.green { 
          border-left:4px solid var(--ti-green); 
        }
        .ti-card--left-accent.indigo { 
          border-left:4px solid var(--ti-primary); 
        }
        .ti-card__hdr {
          display: flex;
          align-items: center;
          gap: 6px; /* espacio entre título y botón */
        }

        .ti-card__hdr h5 {
          margin: 0;
          font-size: 15px;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
        }

        .ti-card__hdr .ti-btn--ghost {
          padding: 2px 6px;
          font-size: 14px;
          line-height: 1;
        }

        .ti-btn { 
          background: var(--ti-primary); 
          color:white; 
          border:1px solid var(--ti-primary); 
          border-radius:8px; 
          padding:6px 10px; 
          font-size:13px; 
          font-weight:600; 
          cursor:pointer; 
        }
        .ti-btn:hover { 
          background: var(--ti-primary-600); 
          border-color: var(--ti-primary-600); 
        }
        .ti-btn--ghost { 
          background: transparent; 
          color: var(--ti-text); 
          border-color: var(--ti-border); 
        }
        .ti-btn--ghost:hover { 
          border-color: var(--ti-primary); 
          color: var(--ti-primary); 
        }
        .ti-btn--danger { 
          background:#DC2626; 
          border-color:#DC2626; 
        }
        .ti-btn--danger:hover { 
          background:#B91C1C; 
          border-color:#B91C1C; 
        }
        .ti-btn--link { 
          background:transparent; 
          color:var(--ti-primary); 
          border:none; 
          padding:0 4px; 
        }

        .ti-chip { 
          background:#10B981; 
          color:white; 
          border-radius:6px; 
          padding:2px 6px; 
          font-size:12px; 
          font-weight:700; 
        }
        .ti-contract__row { 
          display:flex; 
          align-items:center; 
          justify-content:space-between; 
          gap:8px; 
        }
        .ti-contract__meta { 
          display:flex; 
          align-items:center; 
          gap:10px; 
        }
        .ti-contract__name { 
          font-weight:600; 
          color:#065F46; 
        }

        .ti-tags { 
          display:flex; 
          flex-direction:column; 
          gap:8px; 
        }
        .ti-tagRow { 
          position:relative; 
          display:flex; 
          align-items:center; 
          gap:6px; 
        }
        .ti-tag { 
          display:inline-flex; 
          align-items:center; 
          gap:8px; 
          padding:6px 10px; 
          border-radius:9999px; 
          font-size:12px; 
          font-weight:600; 
          border:2px solid transparent; 
        }
        .ti-tag.ok { 
          background:#ECFDF5; 
          color:#065F46; 
          border-color:#A7F3D0; 
        }
        .ti-tag.warn { 
          background:#FFFBEB; 
          color:#92400E; 
          border-color:#FDE68A; 
        }
        .ti-tag__txt { 
          max-width: 200px; 
          overflow: hidden; 
          text-overflow: ellipsis; 
          white-space: nowrap; 
        }
        .ti-tag__dot { 
          display:inline-flex; 
          align-items:center; 
          justify-content:center; 
          min-width:20px; 
        }

        .ti-empty { 
          color:#6B7280; 
          font-size:13px; 
        }

        /* === NUEVO: MODAL DE ASIGNACIÓN DE PORTABILIDAD MÓVIL (DISEÑO HORIZONTAL) === */
        
        .portability-assignment-modal {
          max-width: 1200px;
          width: 95vw;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        
        .portability-horizontal-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          height: 100%;
          overflow: hidden;
        }
        
        /* === ESTILOS PARA SECCIÓN DE OPERADORES (IZQUIERDA) === */
        
        .operators-section {
          background: #F8FAFC;
          border-radius: 12px;
          padding: 1.5rem;
          border: 2px solid #E2E8F0;
          overflow-y: auto;
          max-height: 60vh;
        }
        
        .assignments-section {
          background: #F0FDF4;
          border-radius: 12px;
          padding: 1.5rem;
          border: 2px solid #D1FAE5;
          overflow-y: auto;
          max-height: 60vh;
        }
        
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #E5E7EB;
        }
        
        .section-header h4 {
          margin: 0;
          color: #1F2937;
          font-size: 1.2rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .assignments-count {
          background: #10B981;
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
        }
        
        /* === FORMULARIO CRUD === */
        
        .operator-form {
          background: white;
          border-radius: 10px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          border: 2px solid #6366F1;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.1);
        }
        
        .operator-form h5 {
          margin: 0 0 1rem;
          color: #6366F1;
          font-size: 1rem;
          font-weight: 600;
        }
        
        .form-column {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .form-group label {
          font-weight: 500;
          color: #374151;
          font-size: 0.9rem;
        }
        
        .form-input {
          padding: 0.75rem;
          border: 2px solid #E5E7EB;
          border-radius: 8px;
          font-size: 1rem;
          transition: all 0.2s ease;
          width: 100%; /* Asegurar que no se salga del contenedor */
          box-sizing: border-box;
        }
        
        .form-input:focus {
          outline: none;
          border-color: #6366F1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }
        
        .form-input-disabled {
          background: #F3F4F6 !important;
          color: #6B7280 !important;
          cursor: not-allowed !important;
          border-color: #D1D5DB !important;
        }
        
        .field-note {
          font-size: 0.8rem;
          color: #6B7280;
          font-style: italic;
          margin-top: 0.25rem;
        }
        
        .form-actions {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
        }
        
        /* === LISTA DE OPERADORES === */
        
        .operators-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        
        .empty-operators {
          text-align: center;
          padding: 2rem;
          color: #6B7280;
          background: white;
          border-radius: 10px;
          border: 2px dashed #D1D5DB;
        }
        
        .empty-operators p {
          margin: 0.5rem 0;
        }
        
        .empty-operators .hint {
          font-size: 0.9rem;
          font-style: italic;
        }
        
        .operator-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem;
          background: white;
          border-radius: 10px;
          border: 2px solid #E5E7EB;
          transition: all 0.3s ease;
          position: relative;
        }
        
        .operator-item:hover {
          border-color: #6366F1;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
          transform: translateY(-1px);
        }
        
        .operator-item.assigned {
          background: linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%);
          border-color: #10B981;
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.1);
        }
        
        .operator-item.editing {
          background: linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%);
          border-color: #6366F1;
          box-shadow: 0 4px 16px rgba(99, 102, 241, 0.2);
        }
        
        .operator-item.editing::before {
          content: '✏️';
          position: absolute;
          top: -8px;
          right: -8px;
          background: #6366F1;
          color: white;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: bold;
        }
        
        .operator-info {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex: 1;
        }
        
        .operator-icon {
          font-size: 1.8rem;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
        }
        
        .operator-details {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        
        .operator-name {
          font-weight: 700;
          color: #1F2937;
          font-size: 1.1rem;
        }
        
        .operator-default {
          color: #6B7280;
          font-size: 0.85rem;
          font-weight: 500;
        }
        
        .operator-actions {
          display: flex;
          gap: 0.5rem;
          flex-shrink: 0;
        }
        
        /* === BOTONES DE ACCIÓN === */
        
        .btn-xs {
          padding: 0.4rem 0.6rem;
          font-size: 0.75rem;
          font-weight: 600;
          border-radius: 6px;
          border: 1px solid transparent;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 32px;
          height: 32px;
        }
        
        .btn-success {
          background: #10B981;
          color: white;
          border-color: #10B981;
        }
        
        .btn-success:hover {
          background: #059669;
          transform: scale(1.05);
        }
        
        .btn-xs:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none !important;
        }
        
        /* === SECCIÓN DE ASIGNACIONES (DERECHA) === */
        
        .assignments-container {
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        
        .empty-assignments {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          text-align: center;
          color: #6B7280;
          padding: 2rem;
        }
        
        .empty-assignments .empty-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
          opacity: 0.5;
        }
        
        .empty-assignments h5 {
          margin: 0 0 0.5rem;
          color: #374151;
          font-size: 1.2rem;
        }
        
        .empty-assignments p {
          margin: 0;
          font-size: 0.9rem;
          max-width: 300px;
        }
        
        .assignments-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 1.5rem;
          flex: 1;
          overflow-y: auto;
        }
        
        .assignment-item {
          background: white;
          border: 2px solid #10B981;
          border-radius: 12px;
          padding: 1.5rem;
          position: relative;
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.1);
          transition: all 0.2s ease;
        }
        
        .assignment-item:hover {
          box-shadow: 0 4px 16px rgba(16, 185, 129, 0.15);
          transform: translateY(-1px);
        }
        
        .assignment-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1rem;
        }
        
        .operator-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: #10B981;
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.9rem;
        }
        
        .btn-remove-assignment {
          background: #DC2626;
          color: white;
          border: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          cursor: pointer;
          font-size: 1rem;
          font-weight: bold;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        
        .btn-remove-assignment:hover {
          background: #B91C1C;
          transform: scale(1.1);
        }
        
        .amount-input-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .amount-input-group label {
          font-weight: 500;
          color: #374151;
          font-size: 0.9rem;
        }
        
        .amount-input-wrapper {
          display: flex;
          align-items: center;
          position: relative;
        }
        
        .currency-symbol {
          position: absolute;
          left: 12px;
          color: #6B7280;
          font-weight: 600;
          font-size: 1rem;
        }
        
        .amount-input {
          width: 100%;
          padding: 0.75rem 0.75rem 0.75rem 2rem;
          border: 2px solid #E5E7EB;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          background: #F9FAFB;
          transition: all 0.2s ease;
        }
        
        .amount-input:focus {
          outline: none;
          border-color: #6366F1;
          background: white;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }
        
        /* REMOVIDO: Estilos del resumen que ya no se usa */
        
        .assignments-list {
          flex: 1;
          overflow-y: auto;
          padding-right: 0.5rem; /* Espacio para scrollbar */
        }
        
        .summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem 0;
          color: #1F2937;
        }
        
        .summary-item:not(:last-child) {
          border-bottom: 1px solid rgba(99, 102, 241, 0.2);
        }
        
        /* === RESPONSIVIDAD === */
        
        @media (max-width: 1024px) {
          .portability-assignment-modal {
            max-width: 95vw;
            width: 95vw;
          }
          
          .portability-horizontal-layout {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
          
          .operators-section,
          .assignments-section {
            max-height: 40vh;
          }
        }
        
        @media (max-width: 768px) {
          .form-column {
            gap: 0.75rem;
          }
          
          .operator-form {
            padding: 1rem;
          }
          
          .operator-item {
            flex-direction: column;
            gap: 1rem;
            align-items: flex-start;
          }
          
          .operator-actions {
            align-self: stretch;
            justify-content: space-evenly;
          }
          
          .section-header {
            flex-direction: column;
            gap: 0.5rem;
            align-items: flex-start;
          }
        }
        
        /* === MODAL DE ASIGNACIÓN DE COLECCIONES DE CÓDIGOS === */
        
        .bonus-codes-assignment-modal {
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
        }
        
        .collection-selection h4 {
          margin: 0 0 1rem;
          color: #374151;
          font-size: 1.1rem;
        }
        
        .collection-options {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          max-height: 300px;
          overflow-y: auto;
          padding: 0.5rem;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: #fafafa;
        }
        
        .collection-option {
          background: white;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          transition: all 0.2s ease;
        }
        
        .collection-option:hover {
          border-color: #6366F1;
          box-shadow: 0 2px 4px rgba(99, 102, 241, 0.1);
        }
        
        .collection-radio {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          cursor: pointer;
          width: 100%;
        }
        
        .collection-radio input[type="radio"] {
          display: none;
        }
        
        .collection-radio input[type="radio"]:checked + .radio-custom {
          border-color: #6366F1;
          background: #6366F1;
        }
        
        .collection-radio input[type="radio"]:checked + .radio-custom::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 8px;
          height: 8px;
          background: white;
          border-radius: 50%;
        }
        
        .collection-label {
          flex: 1;
          font-weight: 500;
          color: #374151;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .collection-label.no-assignment {
          color: #6b7280;
          font-style: italic;
        }
        
        .no-collections-message {
          text-align: center;
          padding: 2rem;
          color: #6b7280;
          background: #fef3c7;
          border-radius: 8px;
          border: 1px solid #fbbf24;
        }
        
        .no-collections-message p {
          margin: 0.5rem 0;
        }
        
        .no-collections-message .hint {
          font-size: 0.9rem;
          color: #92400e;
          font-style: italic;
        }

        @media (max-width: 900px) {
          .ti-grid { 
            grid-template-columns:1fr; 
          }
          .ti-contract__row { 
            flex-direction:column; 
            align-items:flex-start; 
            gap:10px; 
          }
        }
        .entidades-container {
          /* Estilos generales si son necesarios */
        }
        .entities-layout {
          display: grid;
          grid-template-columns: 300px 1fr; /* Columna izquierda fija, derecha flexible */
          gap: 1.5rem;
          align-items: start; /* Alinea las columnas en la parte superior */
        }
        .company-list-column, .details-column {
          background-color: #fff;
          border-radius: 8px;
          padding: 1.5rem;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          height: calc(100vh - 140px); /* Ajustar altura según header/footer */
          overflow-y: auto;
        }
        .column-title {
          margin: 0;
          color: #333;
          font-size: 1.25rem;
        }
        .company-list {
          list-style: none;
          padding: 0;
          margin: 1rem 0 0;
        }
        .company-list-item {
          padding: 0.75rem 1rem;
          margin-bottom: 0.5rem;
          border-radius: 6px;
          cursor: pointer;
          transition: background-color 0.2s ease, color 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #555;
        }
        .company-list-item:hover {
          background-color: #f0f4f8;
        }
        .company-list-item.active {
          background-color: #e6effd; /* Azul suave */
          color: #0056b3; /* Azul más oscuro */
          font-weight: 600;
        }
        .icon-list { /* Estilo para iconos en la lista */
          width: 20px;
          height: 20px;
          color: #666;
        }
        .company-list-item.active .icon-list {
           color: #0056b3;
        }

        .details-column {
          /* Estilos específicos para la columna de detalles */
        }
        .empty-state-details {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          text-align: center;
          color: #777;
        }
        .empty-state-details .icon-lg {
          font-size: 3rem;
          margin-bottom: 1rem;
          display: inline-block; /* Para que la animación funcione si se añade */
          animation: bounce 1.5s infinite ease-in-out; /* Animación sutil */
        }
        .selected-company-title {
          color: #1a2b4d; /* Azul oscuro */
          margin: 0 0 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #eee;
        }
        .view-selector {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
          border-bottom: 2px solid #eee;
          padding-bottom: 0.5rem;
        }
        .btn-view-mode {
          background: none;
          border: none;
          padding: 0.5rem 1rem;
          cursor: pointer;
          font-size: 1rem;
          color: #666;
          border-bottom: 3px solid transparent;
          transition: color 0.2s ease, border-color 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }
        .btn-view-mode:hover {
          color: #333;
        }
        .btn-view-mode.active {
          color: #0066cc; /* Azul primario */
          font-weight: 600;
          border-bottom-color: #0066cc;
        }
        .details-content {
          /* Estilos para el contenido de marcas/tiendas */
        }
         .details-subtitle {
           margin: 0;
           color: #555;
           font-size: 1.1rem;
        }
        .item-list {
           list-style: none;
           padding: 0;
           margin-top: 1rem;
        }
        .item-list-entry {
           background-color: #f9f9f9;
           padding: 0.6rem 1rem;
           margin-bottom: 0.5rem;
           border-radius: 4px;
           border: 1px solid #eee;
           font-size: 0.95rem;
           color: #444;
        }
        .empty-state, .empty-state-small {
           color: #888;
           text-align: center;
           padding: 1rem;
        }
        .empty-state-small {
            font-size: 0.9rem;
            padding: 0.5rem;
        }
        .icon-sm { /* Iconos pequeños en botones, etc */
           width: 18px;
           height: 18px;
        }
        .btn-sm { /* Botones pequeños */
            padding: 0.3rem 0.8rem;
            font-size: 0.85rem;
        }
        .loading-spinner-small {
            border: 3px solid #f3f3f3; /* Light grey */
            border-top: 3px solid #0066cc; /* Blue */
            border-radius: 50%;
            width: 24px;
            height: 24px;
            animation: spin 1s linear infinite;
            margin: 1rem auto; /* Centrar spinner de carga */
        }
        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fadeIn 0.4s ease-out;
        }
        @keyframes bounce { /* Animación para el icono de estado vacío */
          0%, 20%, 50%, 80%, 100% { transform: translateX(0); }
          40% { transform: translateX(-15px); }
          60% { transform: translateX(10px); }
        }
        
        /* Media Query para pantallas más pequeñas */
        @media (max-width: 992px) {
          .entities-layout {
            grid-template-columns: 1fr; /* Apilar columnas */
          }
          .company-list-column {
             height: auto; /* Altura automática */
             max-height: 40vh; /* Limitar altura */
             margin-bottom: 1.5rem;
          }
          .details-column {
            height: auto;
          }
        }

        /* Estilos específicos para el overlay del modal de éxito (si son necesarios) */
        .success-message-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.4); /* Fondo semitransparente */
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1040; /* Ligeramente debajo del modal de edición por si acaso, aunque se cierra antes */
        }

        // Nuevos estilos para botones y layout de items
        .company-list-item,
        .item-list-entry {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-right: 0.5rem; /* Espacio para botones */
        }

        .item-text {
           flex-grow: 1;
           cursor: default; /* Quitar cursor pointer si no es para seleccionar */
           margin-right: 0.5rem;
        }
        .company-list-item > .item-text {
            cursor: pointer; /* Mantener cursor para seleccionar compañía */
        }

        .item-actions {
          display: flex;
          gap: 0.5rem;
          flex-shrink: 0; /* Evitar que los botones se encojan */
        }

        .btn-icon {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.2rem;
          font-size: 1.1rem; /* Ajustar tamaño icono */
          line-height: 1;
          color: #666;
          transition: color 0.2s ease;
        }
        .btn-icon:hover {
          color: #333;
        }
        .btn-icon-danger:hover {
            color: #dc3545; /* Rojo para eliminar */
        }
        
        /* Estilos para indicadores y acciones de contratos */
        .brand-card {
          border: 2px solid transparent;
          transition: all 0.3s ease;
        }
        
        .brand-card.has-contract {
          border-color: #10B981;
          background: linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 100%);
        }
        
        .brand-card.no-contract {
          border-color: #F59E0B;
          background: linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%);
        }
        
        .brand-name-section {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 8px;
        }
        
        .contract-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          font-size: 16px;
          flex-shrink: 0;
        }
        
        .contract-assigned {
          background: #10B981;
          color: white;
          animation: pulse 2s infinite;
        }
        
        .contract-missing {
          background: #F59E0B;
          color: white;
          animation: shake 3s infinite;
        }
        
        .contract-loading {
          background: #6B7280;
          color: white;
          animation: spin 1s linear infinite;
        }
        
        .contract-info {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid rgba(16, 185, 129, 0.2);
          border-radius: 8px;
          margin-bottom: 8px;
        }
        
        .contract-name {
          font-size: 14px;
          font-weight: 500;
          color: #065F46;
        }
        
        .contract-version {
          background: #10B981;
          color: white;
          padding: 2px 6px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
        }
        
        .brand-actions {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .contract-actions {
          display: flex;
          gap: 4px;
        }
        
        .brand-edit-actions {
          display: flex;
          gap: 4px;
        }
        
        .btn-contract-action {
          background: none;
          border: 1px solid #D1D5DB;
          cursor: pointer;
          padding: 6px;
          font-size: 14px;
          border-radius: 6px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
        }
        
        .btn-assign {
          background: linear-gradient(135deg, #10B981 0%, #059669 100%);
          color: white;
          border-color: #10B981;
        }
        
        .btn-assign:hover {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          transform: scale(1.05);
        }
        
        .btn-change {
          background: linear-gradient(135deg, #6366F1 0%, #4F46E5 100%);
          color: white;
          border-color: #6366F1;
        }
        
        .btn-change:hover {
          background: linear-gradient(135deg, #4F46E5 0%, #4338CA 100%);
          transform: scale(1.05);
        }
        
        .btn-remove {
          background: linear-gradient(135deg, #DC2626 0%, #B91C1C 100%);
          color: white;
          border-color: #DC2626;
        }
        
        .btn-remove:hover {
          background: linear-gradient(135deg, #B91C1C 0%, #991B1B 100%);
          transform: scale(1.05);
        }
        
        .btn-contract-action:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none !important;
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.8;
          }
        }
        
        @keyframes shake {
          0%, 100% {
            transform: translateX(0);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: translateX(-2px);
          }
          20%, 40%, 60%, 80% {
            transform: translateX(2px);
          }
        }
        }

        // Ajustar para que el error del modal se muestre
        .modal-content .error-message {
            color: #dc3545;
            font-size: 0.9rem;
            margin-top: -0.5rem; /* Acercar al input */
            margin-bottom: 1rem;
        }

        /* === ESTILOS PARA CATEGORÍAS === */
        
        /* Contenedor de lista de marcas */
        .brands-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        /* Tarjeta de marca expandible */
        .brand-card {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .brand-card.expanded {
          border-color: #3b82f6;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
        }

        /* Header de la marca */
        .brand-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: white;
        }

        .brand-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex: 1;
        }

        .brand-name {
          font-weight: 600;
          color: #1f2937;
          font-size: 1rem;
        }

        .btn-expand {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 6px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .btn-expand:hover {
          background: #f3f4f6;
        }

        .btn-expand:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .expand-icon {
          font-size: 1.2rem;
          transition: transform 0.3s ease;
        }

        .expand-icon.rotated {
          transform: rotate(180deg);
        }

        .mini-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #e5e7eb;
          border-top: 2px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        /* Contenedor de categorías expandido */
        .brand-categories {
          padding: 0 1rem 1rem;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          animation: slideDown 0.3s ease;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            max-height: 0;
            padding: 0 1rem;
          }
          to {
            opacity: 1;
            max-height: 500px;
            padding: 0 1rem 1rem;
          }
        }

        /* Grid de categorías */
        .categories-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
        }

        .category-section {
          background: white;
          border-radius: 6px;
          padding: 1rem;
          border: 1px solid #e5e7eb;
        }

        .buyback-section {
          border-left: 4px solid #10b981;
        }

        .sell-section {
          border-left: 4px solid #3b82f6;
        }

        .category-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }

        .category-title {
          margin: 0;
          font-size: 0.9rem;
          font-weight: 600;
          color: #374151;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .category-icon {
          font-size: 1rem;
        }

        .btn-add-category {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 1rem;
          transition: all 0.2s ease;
        }

        .btn-add-category:hover {
          background: #f3f4f6;
        }

        /* Tags de categorías */
        .category-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .category-tag {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 500;
          color: white;
          position: relative;
        }

        .buyback-tag {
          background: #10b981;
        }

        .sell-tag {
          background: #3b82f6;
        }

        .no-categories {
          color: #6b7280;
          font-style: italic;
          font-size: 0.85rem;
        }

        /* === ESTILOS PARA MODAL DE CATEGORÍAS === */

        .category-modal {
          max-width: 700px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          margin-bottom: 1.5rem;
          padding: 0 1rem;
        }

        .modal-subtitle {
          color: white;
          font-size: 0.9rem;
          margin: 0.5rem 0 0;
          font-weight: 500;
        }

        .modal-loading {
          text-align: center;
          padding: 2rem;
        }

        .loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #e5e7eb;
          border-top: 3px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        .modal-body {
          margin-bottom: 1.5rem;
          padding: 0 1rem;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .section-header h4 {
          margin: 0;
          color: #374151;
          font-size: 1rem;
        }

        /* Input para nueva categoría */
        .new-category-input {
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: #f8fafc;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
        }

        .input-group {
          display: flex;
          gap: 0.5rem;
        }

        .input-group .form-input {
          flex: 1;
        }

        /* Grid de checkboxes */
        .categories-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        .category-checkbox {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          background: #f8fafc;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .category-checkbox:hover {
          background: #f0f9ff;
          border-color: #3b82f6;
        }

        .category-checkbox input[type="checkbox"] {
          display: none;
        }

        .checkbox-custom {
          width: 18px;
          height: 18px;
          border: 2px solid #d1d5db;
          border-radius: 4px;
          position: relative;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .category-checkbox input[type="checkbox"]:checked + .checkbox-custom {
          background: #3b82f6;
          border-color: #3b82f6;
        }

        .category-checkbox input[type="checkbox"]:checked + .checkbox-custom::after {
          content: '✓';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: white;
          font-size: 12px;
          font-weight: bold;
        }

        .category-label {
          font-weight: 500;
          color: #374151;
          flex: 1;
        }

        /* Resumen de selección */
        .selected-summary {
          border-top: 1px solid #e5e7eb;
          padding-top: 1rem;
        }

        .selected-summary h5 {
          margin: 0 0 0.75rem;
          color: #374151;
          font-size: 0.9rem;
        }

        .selected-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .selected-tags .category-tag {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding-right: 0.5rem;
        }

        .remove-tag {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          font-size: 1rem;
          font-weight: bold;
          padding: 0;
          margin: 0;
          line-height: 1;
        }

        .remove-tag:hover {
          opacity: 0.7;
        }

        .no-selection {
          color: #6b7280;
          font-style: italic;
          font-size: 0.85rem;
          margin: 0;
        }

        /* === ESTILOS PARA ASIGNACIÓN DE CATÁLOGOS === */
        
        /* Contenedor de categorías con información de catálogos */
        .category-tags.catalog-aware {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        
        .category-item-with-catalog {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          padding: 0.75rem;
          background: white;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          position: relative;
        }
        
        .category-item-with-catalog:hover {
          border-color: #10b981;
          box-shadow: 0 2px 4px rgba(16, 185, 129, 0.1);
        }
        
        /* Tag de categoría mejorado */
        .category-tag.has-catalog {
          background: #10b981;
          border: 2px solid #059669;
        }
        
        .category-tag.no-catalog {
          background: #f59e0b;
          border: 2px solid #d97706;
        }
        
        .category-tag {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem 0.75rem;
          min-width: 120px;
        }
        
        .category-name {
          flex: 1;
        }
        
        .catalog-status {
          margin-left: 0.5rem;
          font-size: 1rem;
        }
        
        .catalog-loading {
          animation: spin 1s linear infinite;
        }
        
        .catalog-assigned {
          color: white;
          font-weight: bold;
        }
        
        .catalog-missing {
          font-size: 0.9rem;
        }
        
        /* Información del catálogo asignado */
        .catalog-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.25rem 0.5rem;
          background: #f0fdf4;
          border-radius: 4px;
          border-left: 3px solid #10b981;
        }
        
        .catalog-name {
          font-size: 0.85rem;
          color: #065f46;
          font-weight: 500;
        }
        
        /* Botón de asignación */
        .btn-assign-catalog {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 4px;
          font-size: 1rem;
          transition: all 0.2s ease;
          opacity: 0.7;
        }
        
        .btn-assign-catalog:hover {
          opacity: 1;
          background: rgba(16, 185, 129, 0.1);
        }
        
        .btn-assign-catalog:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        
        /* === MODAL DE ASIGNACIÓN DE CATÁLOGOS === */
        
        .catalog-assignment-modal {
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
        }
        
        .assignment-info {
          background: #f8fafc;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          border: 1px solid #e2e8f0;
        }
        
        .info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem 0;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .info-row:last-child {
          border-bottom: none;
        }
        
        .info-row.current-assignment {
          background: #ecfdf5;
          margin: 0.5rem -1rem 0;
          padding: 0.75rem 1rem;
          border-radius: 6px;
          border: 1px solid #10b981;
        }
        
        .info-label {
          font-weight: 600;
          color: #374151;
        }
        
        .info-value {
          color: #6b7280;
        }
        
        .info-value.current {
          color: #065f46;
          font-weight: 600;
        }
        
        .catalog-selection h4 {
          margin: 0 0 1rem;
          color: #374151;
          font-size: 1.1rem;
        }
        
        .catalog-options {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          max-height: 300px;
          overflow-y: auto;
          padding: 0.5rem;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: #fafafa;
        }
        
        .catalog-option {
          background: white;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          transition: all 0.2s ease;
        }
        
        .catalog-option:hover {
          border-color: #3b82f6;
          box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
        }
        
        .catalog-radio {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          cursor: pointer;
          width: 100%;
        }
        
        .catalog-radio input[type="radio"] {
          display: none;
        }
        
        .radio-custom {
          width: 20px;
          height: 20px;
          border: 2px solid #d1d5db;
          border-radius: 50%;
          position: relative;
          transition: all 0.2s ease;
        }
        
        .catalog-radio input[type="radio"]:checked + .radio-custom {
          border-color: #3b82f6;
          background: #3b82f6;
        }
        
        .catalog-radio input[type="radio"]:checked + .radio-custom::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 8px;
          height: 8px;
          background: white;
          border-radius: 50%;
        }
        
        .catalog-label {
          flex: 1;
          font-weight: 500;
          color: #374151;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .catalog-label.no-assignment {
          color: #6b7280;
          font-style: italic;
        }
        
        .current-badge {
          background: #10b981;
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-left: auto;
        }
        
        .no-catalogs-message {
          text-align: center;
          padding: 2rem;
          color: #6b7280;
          background: #fef3c7;
          border-radius: 8px;
          border: 1px solid #fbbf24;
        }
        
        .no-catalogs-message p {
          margin: 0.5rem 0;
        }
        
        .no-catalogs-message .hint {
          font-size: 0.9rem;
          color: #92400e;
          font-style: italic;
        }
        
        /* Responsividad */
        @media (max-width: 768px) {
          .categories-grid {
            grid-template-columns: 1fr;
          }
          
          .category-modal {
            max-width: 95vw;
            margin: 1rem;
          }
          
          .catalog-assignment-modal {
            max-width: 95vw;
            margin: 1rem;
          }
          
          .section-header {
            flex-direction: column;
            gap: 0.5rem;
            align-items: flex-start;
          }
          
          .input-group {
            flex-direction: column;
          }
          
          .category-tags.catalog-aware {
            gap: 0.5rem;
          }
          
          .catalog-options {
            max-height: 250px;
          }
        }

      `}</style>
    </div>
  );
}












export default Entidades;